import { useEffect, useState } from "react";
import { BrowserRouter, Routes, Route, NavLink, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import "./styles.css";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const WHATSAPP_NUMBER = "918008079304";

const SCENES = [
  {
    kind: "intro",
    eye: "SASXL Clinical Research Training",
    title: "Launch Your SAS Clinical Career\nwith Confidence.",
    sub: "At SASXL, we believe training should lead to employment — not just certificates.",
  },
  {
    kind: "feeHero",
    eye: "Placement-Linked Fee Structure",
    title: "Pay 25% now.\nThe rest only after your job offer.",
    sub: "Skin in the game from both sides. We don't get paid in full until you do.",
    rows: [
      ["25%", "At Enrollment", "Lock your seat in the next cohort"],
      ["—", "Training + Placement", "6 months of dedicated training & interview prep"],
      ["75%", "After Job Offer", "Pay the remaining only after you receive your Offer Letter"],
    ],
  },
  {
    kind: "chips",
    eye: "Program Includes",
    title: "Everything you need.\nNothing you don't.",
    chips: ["SAS Clinical Programming", "SDTM & ADaM Development", "Industry Projects", "Interview Readiness", "Placement Assistance", "1:1 Mentorship"],
  },
  {
    kind: "stat",
    eye: "Industry-led",
    big: "12+",
    title: "Years of pharma submission experience teaching every class.",
    sub: "Not a course recorder. A live, working CDISC programmer who's filed with the FDA.",
  },
  {
    kind: "code",
    eye: "Real code · real datasets",
    title: "Write production-grade SAS, day one.",
    code: [
      ["k", "data"], ["", " adsl; "],
      ["k", "set"], ["", " sdtm.dm; "],
      ["", "\n  "], ["k", "if"], ["", " "], ["s", "saffl"], ["", "="], ["s", "'Y'"], ["", "; "],
      ["", "\n  "], ["k", "trtfl"], ["", "="], ["s", "'Y'"], ["", "; "],
      ["", "\nrun;"],
    ],
  },
  {
    kind: "stages",
    eye: "6 Months · Dedicated Focus",
    title: "From zero coding → first offer.",
    sub: "Six intense months. Same world-class curriculum. Engineered for faster outcomes.",
    stages: ["Mo 1 Trials + SAS", "Mo 2 SDTM", "Mo 3 ADaM + TLF", "Mo 4 Projects", "Mo 5 Resume + LinkedIn", "Mo 6 Interview + Offer"],
  },
  {
    kind: "pillars",
    eye: "What makes SASXL different",
    title: "Built around your career,\nnot our calendar.",
    pillars: [
      ["📍", "Limited Batch Size", "Personal attention guaranteed"],
      ["📍", "Personalized Mentorship", "1:1 calls with working CDISC programmers"],
      ["📍", "Career-Focused Training", "Every module ladders to interview-ready"],
    ],
  },
  {
    kind: "cta",
    eye: "Your success is our priority",
    title: "Pay Majority of Fees\nAfter Receiving Job Offer.",
    sub: "Book a free 1:1 demo. No payment. No commitment. Just clarity on the placement model.",
    cta: "Book Free Demo on WhatsApp",
  },
];
const SCENE_DURATION = 5500; // ms

function CinemaShowcase() {
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    const t = setTimeout(() => setIdx((i) => (i + 1) % SCENES.length), SCENE_DURATION);
    return () => clearTimeout(t);
  }, [idx]);

  const s = SCENES[idx];
  const progressKey = `${idx}`;

  return (
    <div className="demo-wrap" data-testid="demo-video">
      <div className="cinema">
        <div className="cinema-grid"></div>
        <div className="cinema-orb a"></div>
        <div className="cinema-orb b"></div>
        <div className="cinema-badge"><span className="live"></span> Auto-playing showcase</div>
        <div key={progressKey} className="cinema-progress" style={{ animation: `progress ${SCENE_DURATION}ms linear forwards` }}></div>

        {SCENES.map((scene, i) => (
          <div key={i} className={`scene ${i === idx ? "active" : ""}`} data-testid={`cinema-scene-${i}`}>
            <div className="scene-inner">
              <div className="eye">{scene.eye}</div>
              {scene.kind === "stat" && <div className="big">{scene.big}</div>}
              <h2 style={{ whiteSpace: "pre-line" }}>{scene.title}</h2>
              {scene.sub && <p>{scene.sub}</p>}
              {scene.kind === "chips" && (
                <div className="small-grid">
                  {scene.chips.map((c, j) => <div className="chip" key={j} style={{ animationDelay: `${0.7 + j * 0.08}s` }}>{c}</div>)}
                </div>
              )}
              {scene.kind === "code" && (
                <pre className="code-block">{scene.code.map(([cls, txt], k) => <span key={k} className={cls}>{txt}</span>)}</pre>
              )}
              {scene.kind === "stages" && (
                <div className="stage-row">
                  {scene.stages.map((st, j) => <span key={j} className={`stage ${j === 5 ? "hot" : ""}`}>{st}</span>)}
                </div>
              )}
              {scene.kind === "feeHero" && (
                <div className="fee-rows">
                  {scene.rows.map(([amt, lbl, det], j) => (
                    <div className="fee-row" key={j} style={{ animationDelay: `${0.7 + j * 0.15}s` }}>
                      <div className="fee-amt">{amt}</div>
                      <div className="fee-text"><strong>{lbl}</strong><span>{det}</span></div>
                    </div>
                  ))}
                </div>
              )}
              {scene.kind === "pillars" && (
                <div className="pillar-row">
                  {scene.pillars.map(([ic, ttl, det], j) => (
                    <div className="pillar" key={j} style={{ animationDelay: `${0.7 + j * 0.15}s` }}>
                      <div className="pi">{ic}</div>
                      <strong>{ttl}</strong>
                      <span>{det}</span>
                    </div>
                  ))}
                </div>
              )}
              {scene.kind === "cta" && (
                <button className="ctab" data-testid="cinema-cta" onClick={() => openWA("Hi SASXL! I'd like to book my free demo class. I just watched the showcase.")}>
                  <i className="fa-brands fa-whatsapp"></i> {scene.cta}
                </button>
              )}
            </div>
          </div>
        ))}

        <div className="cinema-controls">
          {SCENES.map((_, i) => (
            <button key={i} className={`cinema-dot ${i === idx ? "active" : ""}`} data-testid={`cinema-dot-${i}`} onClick={() => setIdx(i)} aria-label={`Scene ${i + 1}`}></button>
          ))}
        </div>
      </div>
      <div className="demo-meta">
        <div>
          <h3>Why SASXL — A 45-Second Look</h3>
          <p>Auto-playing animated showcase · click dots to jump</p>
        </div>
        <button className="btn btn-whatsapp" data-testid="demo-book-cta" onClick={() => openWA("Hi SASXL! I'd like to book a live demo class.")}>
          <i className="fa-brands fa-whatsapp"></i> Book Live Demo
        </button>
      </div>
    </div>
  );
}
// Placeholder YouTube demo video (user will replace later). Rick-roll-safe educational SAS clip placeholder.
// (Reserved — currently using animated CinemaShowcase below.)

const waUrl = (msg) => `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(msg)}`;
const openWA = (msg) => window.open(waUrl(msg), "_blank", "noopener");

function useReveal() {
  useEffect(() => {
    const els = document.querySelectorAll(".reveal:not(.in)");
    if (!("IntersectionObserver" in window)) {
      els.forEach((e) => e.classList.add("in"));
      return;
    }
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        if (e.isIntersecting) { e.target.classList.add("in"); io.unobserve(e.target); }
      });
    }, { threshold: 0.12 });
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  });
}

function Toast({ msg, kind, onClose }) {
  useEffect(() => { const t = setTimeout(onClose, 3500); return () => clearTimeout(t); }, [onClose]);
  return <div className={`toast ${kind === "err" ? "err" : ""}`} data-testid="toast">{msg}</div>;
}

function Header({ openLead }) {
  const [open, setOpen] = useState(false);
  const loc = useLocation();
  useEffect(() => setOpen(false), [loc.pathname]);
  const link = ({ isActive }) => isActive ? "active" : "";
  const nav = useNavigate();
  return (
    <header className="header" data-testid="site-header">
      <div className="container nav">
        <div className="logo" data-testid="logo" onClick={() => nav("/")}>
          <span className="logo-mark">SX</span>
          <span>SAS<span style={{ color: "var(--brand-primary)" }}>XL</span></span>
        </div>
        <nav className={`nav-links ${open ? "open" : ""}`} data-testid="nav-links">
          <NavLink to="/" end className={link} data-testid="nav-home">Home</NavLink>
          <NavLink to="/courses" className={link} data-testid="nav-courses">Courses</NavLink>
          <NavLink to="/career" className={link} data-testid="nav-career">Career</NavLink>
          <NavLink to="/projects" className={link} data-testid="nav-projects">Projects</NavLink>
          <NavLink to="/mentors" className={link} data-testid="nav-mentors">Mentors</NavLink>
          <NavLink to="/contact" className={link} data-testid="nav-contact">Contact</NavLink>
        </nav>
        <button className="btn btn-primary nav-cta" data-testid="nav-book-cta" onClick={() => openLead && openLead("Header CTA")}>
          <i className="fa-solid fa-calendar-check"></i> Book Free Consultation
        </button>
        <button className="mobile-toggle" data-testid="mobile-menu-toggle" onClick={() => setOpen((v) => !v)} aria-label="Menu"><i className="fa-solid fa-bars"></i></button>
      </div>
    </header>
  );
}

function Footer() {
  const nav = useNavigate();
  return (
    <footer className="footer" data-testid="footer">
      <div className="container">
        <div className="footer-grid">
          <div data-testid="footer-brand">
            <div className="logo" style={{ marginBottom: "1rem" }} onClick={() => nav("/")}>
              <span className="logo-mark">SX</span>
              <span>SAS<span style={{ color: "var(--brand-primary)" }}>XL</span></span>
            </div>
            <p style={{ color: "var(--text-secondary)", maxWidth: 320, fontSize: "0.92rem" }}>Industry-led Clinical SAS training. From SDTM/ADaM fundamentals to production-grade pharma analytics.</p>
          </div>
          <div><h4>Explore</h4><ul>
            <li><a onClick={() => nav("/courses")} data-testid="footer-link-courses">Courses</a></li>
            <li><a onClick={() => nav("/career")} data-testid="footer-link-career">Career Roadmap</a></li>
            <li><a onClick={() => nav("/placement")} data-testid="footer-link-placement">Placement Support</a></li>
            <li><a onClick={() => nav("/projects")} data-testid="footer-link-projects">Real Projects</a></li>
            <li><a onClick={() => nav("/mentors")} data-testid="footer-link-mentors">Mentors</a></li>
            <li><a onClick={() => nav("/resources")} data-testid="footer-link-resources">Free Resources</a></li>
          </ul></div>
          <div><h4>Contact</h4><ul>
            <li><a onClick={() => nav("/contact")} data-testid="footer-link-contact">Get In Touch</a></li>
            <li><a onClick={() => openWA("Hi SASXL!")}>WhatsApp Us</a></li>
            <li><a href="mailto:hello@sasxl.com">hello@sasxl.com</a></li>
            <li><a onClick={() => nav("/privacy-policy")} data-testid="footer-link-privacy">Privacy Policy</a></li>
            <li><a onClick={() => nav("/terms")} data-testid="footer-link-terms">Terms of Service</a></li>
          </ul></div>
          <div><h4>Country</h4><ul>
            <li><a onClick={() => nav("/training/india")} data-testid="footer-link-india">🇮🇳 India</a></li>
            <li><a onClick={() => nav("/training/usa")} data-testid="footer-link-usa">🇺🇸 USA</a></li>
            <li><a onClick={() => nav("/training/uk")} data-testid="footer-link-uk">🇬🇧 UK</a></li>
            <li><a onClick={() => nav("/training/canada")} data-testid="footer-link-canada">🇨🇦 Canada</a></li>
            <li><a onClick={() => nav("/training/australia")} data-testid="footer-link-australia">🇦🇺 Australia</a></li>
          </ul></div>
          <div><h4>Follow</h4><ul>
            <li><a><i className="fa-brands fa-linkedin"></i> LinkedIn</a></li>
            <li><a><i className="fa-brands fa-youtube"></i> YouTube</a></li>
            <li><a><i className="fa-brands fa-instagram"></i> Instagram</a></li>
          </ul></div>
        </div>
        <div className="footer-bottom">
          <span>© {new Date().getFullYear()} SASXL. All rights reserved.</span>
          <span className="mono">v2.0 · Built for learners, by mentors.</span>
        </div>
      </div>
    </footer>
  );
}

function FloatWA() {
  const [open, setOpen] = useState(false);
  const [attention, setAttention] = useState(false);
  const [showBadge, setShowBadge] = useState(false);

  useEffect(() => {
    if (sessionStorage.getItem("wa_attention_done")) return;
    const t = setTimeout(() => {
      setAttention(true);
      setShowBadge(true);
      sessionStorage.setItem("wa_attention_done", "1");
      // Stop bouncing after one cycle (~1.2s) but keep badge until user interacts
      setTimeout(() => setAttention(false), 1500);
    }, 5000);
    return () => clearTimeout(t);
  }, []);

  const handleToggle = () => {
    setOpen((v) => !v);
    setShowBadge(false);
  };

  const quicks = [
    { icon: "fa-rupee-sign", label: "Course fees & EMI options", msg: "Hi SASXL! I'd like to know the course fees, EMI options, and the 25/75 placement-linked fee structure." },
    { icon: "fa-calendar-days", label: "When does the next batch start?", msg: "Hi SASXL! When does the next live cohort start, and how many seats are left?" },
    { icon: "fa-circle-play", label: "Book a free live demo class", msg: "Hi SASXL! I'd like to book a FREE live demo class. Please share available slots." },
    { icon: "fa-graduation-cap", label: "Curriculum & syllabus details", msg: "Hi SASXL! Can you share the detailed curriculum (SDTM, ADaM, TLF, AI Automation, SAS-R) for the 6-month program?" },
    { icon: "fa-briefcase", label: "Placement & job support", msg: "Hi SASXL! How does your placement support work? What companies hire and what's the success rate?" },
    { icon: "fa-user-tie", label: "Talk to a counselor now", msg: "Hi SASXL! I'd like to speak with a counselor about the Clinical SAS program. Please call me back." },
  ];

  return (
    <>
      <button className={`float-wa ${open ? "open" : ""} ${attention ? "wa-bounce" : ""}`} data-testid="float-whatsapp" onClick={handleToggle} title="Chat with us">
        {open ? <i className="fa-solid fa-xmark"></i> : <i className="fa-brands fa-whatsapp"></i>}
        {!open && <span className="float-wa-pulse"></span>}
        {!open && showBadge && <span className="wa-badge" data-testid="wa-badge">1</span>}
      </button>

      {open && (
        <div className="wa-chat" data-testid="wa-chat-widget">
          <div className="wa-chat-head">
            <div className="wa-chat-avatar"><i className="fa-brands fa-whatsapp"></i></div>
            <div className="wa-chat-meta">
              <strong>SASXL Support</strong>
              <span><span className="wa-online-dot"></span> Typically replies within 1 hour</span>
            </div>
            <button className="wa-chat-close" onClick={() => setOpen(false)} data-testid="wa-chat-close" aria-label="Close">×</button>
          </div>

          <div className="wa-chat-body">
            <div className="wa-bubble wa-bubble-them">
              <strong>👋 Hi there!</strong>
              <p>I'm Praveen's team at SASXL. How can I help you today? Pick a topic and we'll continue on WhatsApp.</p>
            </div>

            <div className="wa-quicks">
              {quicks.map((q, i) => (
                <button key={i} className="wa-quick" data-testid={`wa-quick-${i}`} onClick={() => { openWA(q.msg); setOpen(false); }}>
                  <i className={`fa-solid ${q.icon}`}></i>
                  <span>{q.label}</span>
                  <i className="fa-solid fa-arrow-right wa-quick-arrow"></i>
                </button>
              ))}
            </div>

            <button className="wa-typed" data-testid="wa-typed" onClick={() => openWA("Hi SASXL! ")}>
              <i className="fa-solid fa-pen"></i> Type my own message →
            </button>
          </div>

          <div className="wa-chat-foot">
            <span className="mono">🔒 Secure · End-to-end via WhatsApp</span>
          </div>
        </div>
      )}
    </>
  );
}

// === Sticky bottom CTA bar — every page ===
function StickyCTA({ onOpenForm }) {
  const [dismissed, setDismissed] = useState(false);
  if (dismissed) return null;
  return (
    <div className="sticky-cta" data-testid="sticky-cta">
      <div className="sticky-cta-inner">
        <div className="sticky-cta-text">
          <strong>Book Free Career Consultation</strong>
          <span>Talk to an industry expert · No commitment</span>
        </div>
        <div className="sticky-cta-actions">
          <button className="btn btn-primary sticky-btn" data-testid="sticky-cta-form" onClick={onOpenForm}><i className="fa-solid fa-calendar-check"></i> Book Call</button>
          <button className="btn btn-whatsapp sticky-btn" data-testid="sticky-cta-wa" onClick={() => openWA("Hi SASXL! I'd like to book a free career consultation.")}><i className="fa-brands fa-whatsapp"></i> WhatsApp</button>
          <button className="sticky-close" data-testid="sticky-cta-close" onClick={() => setDismissed(true)} aria-label="Dismiss">×</button>
        </div>
      </div>
    </div>
  );
}

// === Quick Lead Form Modal (sticky CTA + exit intent open this) ===
function QuickLeadModal({ open, onClose, source, pushToast, title = "Book Free Career Consultation", subtitle = "We'll call you within 1 business hour." }) {
  const [form, setForm] = useState({ name: "", email: "", phone: "", country: "India", level: "", course: "" });
  const [busy, setBusy] = useState(false);
  if (!open) return null;
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone) { pushToast("Please fill all required fields", "err"); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/leads`, {
        name: form.name, email: form.email, phone: form.phone,
        level: form.level, goal: `${form.course} · ${form.country}`,
        message: `From ${source}`, source,
      });
      pushToast("Got it! We'll call you shortly.");
      onClose();
      setForm({ name: "", email: "", phone: "", country: "India", level: "", course: "" });
    } catch { pushToast("Submission failed. Please WhatsApp us.", "err"); }
    finally { setBusy(false); }
  };
  return (
    <div className="modal-backdrop" data-testid="quick-lead-modal" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="modal-shell">
        <button className="modal-close" data-testid="modal-close" onClick={onClose} aria-label="Close">×</button>
        <span className="eyebrow"><i className="fa-solid fa-bolt"></i> {source}</span>
        <h2 style={{ fontSize: "1.6rem", margin: "1rem 0 0.4rem" }}>{title}</h2>
        <p style={{ color: "var(--text-secondary)", margin: "0 0 1.5rem", fontSize: "0.92rem" }}>{subtitle}</p>
        <form onSubmit={submit}>
          <div className="form-row">
            <div className="field"><label>Full Name *</label><input type="text" required value={form.name} onChange={set("name")} data-testid="ql-name" placeholder="e.g. Anita Verma" /></div>
            <div className="field"><label>Phone *</label><input type="tel" required value={form.phone} onChange={set("phone")} data-testid="ql-phone" placeholder="+91 98XXXXXXXX" /></div>
          </div>
          <div className="form-row">
            <div className="field"><label>Email *</label><input type="email" required value={form.email} onChange={set("email")} data-testid="ql-email" placeholder="you@example.com" /></div>
            <div className="field"><label>Country</label>
              <select value={form.country} onChange={set("country")} data-testid="ql-country">
                <option>India</option><option>USA</option><option>UK</option><option>Canada</option><option>Australia</option><option>UAE</option><option>Other</option>
              </select>
            </div>
          </div>
          <div className="form-row">
            <div className="field"><label>Current Experience</label>
              <select value={form.level} onChange={set("level")} data-testid="ql-level">
                <option value="">Select…</option><option>Fresher</option><option>0–2 yrs</option><option>2–5 yrs</option><option>5+ yrs</option>
              </select>
            </div>
            <div className="field"><label>Interested Course</label>
              <select value={form.course} onChange={set("course")} data-testid="ql-course">
                <option value="">Select…</option><option>Self-Paced (₹9,999)</option><option>Live + Mentorship (₹39,999)</option><option>Career Launch Academy with Internship (₹85,000)</option><option>Not sure yet</option>
              </select>
            </div>
          </div>
          <button type="submit" className="btn btn-primary" style={{ width: "100%", justifyContent: "center", marginTop: "0.5rem" }} data-testid="ql-submit" disabled={busy}>
            <i className="fa-solid fa-rocket"></i> {busy ? "Sending…" : "Get Free Career Roadmap"}
          </button>
          <p style={{ textAlign: "center", color: "var(--text-muted)", fontSize: "0.78rem", marginTop: "1rem" }}>By submitting you agree to our Privacy Policy. We never spam.</p>
        </form>
      </div>
    </div>
  );
}

// === Exit-intent hook ===
function useExitIntent(onExit, enabled = true) {
  useEffect(() => {
    if (!enabled) return;
    if (sessionStorage.getItem("exit_intent_shown")) return;
    let fired = false;
    const handler = (e) => {
      if (fired) return;
      if (e.clientY <= 0) { fired = true; sessionStorage.setItem("exit_intent_shown", "1"); onExit(); }
    };
    document.addEventListener("mouseleave", handler);
    // Mobile fallback: trigger after 60s
    const t = setTimeout(() => { if (!fired) { fired = true; sessionStorage.setItem("exit_intent_shown", "1"); onExit(); } }, 60000);
    return () => { document.removeEventListener("mouseleave", handler); clearTimeout(t); };
  }, [enabled, onExit]);
}

function Layout({ children, openLead }) {
  useReveal();
  return (<><Header openLead={openLead} />{children}<Footer /><FloatWA /><StickyCTA onOpenForm={() => openLead("Sticky CTA")} /></>);
}

// ============ HOME ============
const PHASES_6 = [
  { m: "Month 1", t: "Clinical Research + Base SAS", d: "ICH-GCP, FDA & EMA frameworks. Trial phases. Base & Advanced SAS — DATA step, PROC SQL, macros." },
  { m: "Month 2", t: "SDTM Domain Mapping", d: "Domain mapping (DM, AE, CM, VS, LB). Trial design datasets. SUPPQUAL & RELREC. CDISC controlled terminology." },
  { m: "Month 3", t: "ADaM + TLF Generation", d: "ADSL, ADAE, ADTTE, ADLB. Imputation flags & traceability. PROC REPORT, Kaplan-Meier, forest plots, RTF outputs." },
  { m: "Month 4", t: "Mock + Live Projects", d: "End-to-end SDTM→ADaM→TLF pipelines. Oncology, diabetes & cardiology capstones. Define-XML authoring." },
  { m: "Month 5", t: "Resume + LinkedIn", d: "Pharma-recruiter-friendly resume rebuild. LinkedIn profile audit & headline rewrite. Recruiter-keyword optimization." },
  { m: "Month 6", t: "Mock Interviews + Job Offer", d: "Realistic interviews simulating IQVIA, Parexel & Novartis loops. Referrals. Salary negotiation. Offer letter." },
];

// === Compact Hero Lead Form ===
function HeroLeadForm({ pushToast }) {
  const [form, setForm] = useState({ name: "", email: "", phone: "", country: "India", level: "", course: "" });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone) { pushToast("Please fill name, email & phone", "err"); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/leads`, { name: form.name, email: form.email, phone: form.phone, level: form.level, goal: `${form.course} · ${form.country}`, message: "Hero form", source: "hero-lead-form" });
      pushToast("Got it! We'll send your free roadmap shortly 🚀");
      setForm({ name: "", email: "", phone: "", country: "India", level: "", course: "" });
    } catch { pushToast("Submit failed. Try WhatsApp.", "err"); }
    finally { setBusy(false); }
  };
  return (
    <form className="hero-form" data-testid="hero-lead-form" onSubmit={submit}>
      <div className="hero-form-head">
        <span className="eyebrow"><i className="fa-solid fa-bolt"></i> 60 sec — free roadmap</span>
        <h3 style={{ fontSize: "1.3rem", margin: "0.6rem 0 0.3rem" }}>Get Your <span className="grad">Free Career Roadmap</span></h3>
        <p style={{ fontSize: "0.85rem", color: "var(--text-secondary)", margin: 0 }}>Customized 6-month plan, salary benchmark, and offer letter timeline — straight to your inbox.</p>
      </div>
      <div className="field"><input type="text" required value={form.name} onChange={set("name")} data-testid="hero-form-name" placeholder="Full Name *" /></div>
      <div className="field"><input type="email" required value={form.email} onChange={set("email")} data-testid="hero-form-email" placeholder="Email *" /></div>
      <div className="field"><input type="tel" required value={form.phone} onChange={set("phone")} data-testid="hero-form-phone" placeholder="Phone with country code *" /></div>
      <div className="form-row" style={{ gap: "0.6rem" }}>
        <div className="field"><select value={form.country} onChange={set("country")} data-testid="hero-form-country"><option>India</option><option>USA</option><option>UK</option><option>Canada</option><option>Australia</option><option>UAE</option><option>Other</option></select></div>
        <div className="field"><select value={form.level} onChange={set("level")} data-testid="hero-form-level"><option value="">Experience</option><option>Fresher</option><option>0–2 yrs</option><option>2–5 yrs</option><option>5+ yrs</option></select></div>
      </div>
      <div className="field"><select value={form.course} onChange={set("course")} data-testid="hero-form-course"><option value="">Interested Course</option><option>Self-Paced (₹9,999)</option><option>Live + Mentorship (₹39,999)</option><option>Career Launch Academy with Internship (₹85,000)</option><option>Not sure yet</option></select></div>
      <button type="submit" className="btn btn-primary" style={{ width: "100%", justifyContent: "center", marginTop: "0.25rem" }} disabled={busy} data-testid="hero-form-submit">
        <i className="fa-solid fa-rocket"></i> {busy ? "Sending…" : "Get Free Career Roadmap"}
      </button>
      <p style={{ textAlign: "center", color: "var(--text-muted)", fontSize: "0.72rem", marginTop: "0.6rem" }}>🔒 Your data is safe. We never spam.</p>
    </form>
  );
}

// === Comparison Table data ===
const COMPARE_ROWS = [
  ["Real CDISC Projects", true, "Limited"],
  ["SDTM Domain Mapping (Live)", true, "Basic recordings"],
  ["ADaM Dataset Construction", true, "Basic"],
  ["TLF Generation Workshops", true, "Self-paced only"],
  ["Define-XML Authoring", true, "Rarely taught"],
  ["Pinnacle 21 Validation", true, "No"],
  ["AI + R Automation", true, "Not offered"],
  ["1:1 Personal Mentorship", true, "Generic group calls"],
  ["Resume & LinkedIn Rebuild", true, "Generic templates"],
  ["Mock Interviews · Real Recruiters", true, "Limited"],
  ["Placement Referral Network", true, "No formal network"],
  ["Pay 75% Only After Job Offer", true, "Pay full upfront"],
  ["Lifetime Alumni Community", true, "Limited / paid"],
];

// === Free Resources data ===
const RESOURCES = [
  { id: "interview-qa", icon: "fa-clipboard-question", title: "SAS Clinical Interview Questions", desc: "120+ real interview questions from IQVIA, Parexel, Novartis with model answers.", tag: "PDF · 38 pages" },
  { id: "roadmap", icon: "fa-route", title: "Clinical Programming Roadmap", desc: "Month-by-month skill checklist from fresher → first offer. Use it as your career map.", tag: "PDF · 22 pages" },
  { id: "sdtm-checklist", icon: "fa-list-check", title: "SDTM Mapping Checklist", desc: "Domain-by-domain CDISC mapping rules, common pitfalls, validation tips.", tag: "PDF · 16 pages" },
];

// === Google reviews placeholder data (replace with real embed when ready) ===
const GOOGLE_REVIEWS = [
  ["Aditya M.", 5, "Best Clinical SAS training I've come across. Praveen sir explains every concept with industry context. Highly recommended for serious career-switchers."],
  ["Pratiksha V.", 5, "Joined as a fresher with no SAS background. Got placed at Cognizant in month 6. The placement-linked fee model is genuinely fair."],
  ["Sumit R.", 5, "Real CDISC projects, not just theory. The capstone work directly built my interview portfolio. 5 stars."],
];
const avatar = (seed) => `https://api.dicebear.com/7.x/personas/svg?seed=${encodeURIComponent(seed)}&backgroundColor=0B1021,131B33,1E293B&backgroundType=solid`;
const TESTIMONIALS = [
  ["Sneha A.","Clinical SAS Programmer · IQVIA","Moved from B.Pharm to Clinical SAS Programmer in 5 months. The SDTM/ADaM mock submission practice was a game-changer."],
  ["Rohan K.","Statistical Programmer · Parexel","The mentor calls cleared every doubt instantly. I had zero coding background — now I'm writing macros independently."],
  ["Meera D.","Junior SAS Analyst · Cognizant","Real-project simulation is the differentiator. I cracked 3 interviews before finishing module 8."],
  ["Arun K.","SAS Programmer · Novartis","The placement-linked fee model gave me confidence. Praveen sir's mentorship is unmatched."],
  ["Priyanka S.","Clinical Programmer · IQVIA","From M.Sc Biotech to Clinical SAS in 6 months. The 1:1 calls every Saturday made the difference."],
  ["Vikram R.","Stat Programmer · Syneos","Live SDTM mapping sessions felt like real-world work. Got hired before completing the course."],
  ["Nandini S.","Associate Programmer · TCS","Best decision I made in 2025. SASXL doesn't just teach — they build your career roadmap."],
  ["Kiran P.","Clinical SAS Analyst · Accenture","The mock interview rounds were brutal — exactly what I needed. Passed Cognizant in 2 rounds."],
  ["Shreya S.","SAS Programmer · ICON plc","Praveen sir personally reviewed my resume 4 times. That attention to detail = offer letter."],
  ["Mohan K.","Clinical Programmer · Parexel","I tried 2 other institutes before SASXL. None compare to the hands-on CDISC training here."],
  ["Anjali T.","Statistical Programmer · Quanticate","The 6-month focused format kept me motivated. No fluff, only what's needed for the job."],
  ["Ramesh S.","SAS Analyst · LabCorp","Pay-after-placement model is genuinely fair. Felt like SASXL was invested in my success."],
  ["Deepika V.","Clinical Programmer · Cognizant","Coming from a non-pharma background, I was nervous. The mentors made it impossibly smooth."],
  ["Jayanth K.","Programmer Trainee · IQVIA","Cleared IQVIA interview within 5 months. The capstone projects directly map to real studies."],
  ["Lavanya R.","SAS Programmer · Sciformix","Define-XML authoring class was gold. No other institute teaches it at this depth."],
  ["Gokul H.","Clinical Programmer · Reify Health","Live cohort + 1:1 = magic combo. I always had a mentor to call when stuck."],
  ["Bharath M.","Stat Programmer · Veeda CR","Honest, real curriculum. No empty promises. Placement happened in month 6 as committed."],
  ["Mahima S.","Junior Programmer · Cytel","The interview prep alone is worth the fee. Mock rounds = confidence at the real interview."],
  ["Aakash V.","Clinical SAS · Indegene","Praveen sir's energy in every class is infectious. Made me actually enjoy SDTM mapping."],
  ["Snehal N.","SAS Programmer · Syneos","Career-switching from QA to Clinical SAS at 28 was scary. SASXL made it feel inevitable."],
  ["Harish K.","Programmer I · Parexel","Got 3 offers in 2 weeks after the course. The resume rebuild module is underrated."],
  ["Divya T.","Clinical Programmer · Bioclinica","Personal attention was the highlight. Small batch sizes really do make a difference."],
  ["Rahul M.","SAS Analyst · ClinChoice","Joined as a fresher, finished as a working Clinical SAS Programmer. SASXL delivered exactly that."],
  ["Karthik S.","Stat Programmer · ICON","The placement team's referral network opened doors I couldn't on my own."],
  ["Nikitha P.","Clinical Programmer · IQVIA","I'd recommend SASXL to anyone serious about Clinical SAS — not just curious."],
  ["Vivek S.","SAS Programmer · Cognizant","6 months ago I didn't know what ADaM was. Today I'm building ADSL for a Phase III study."],
  ["Anita R.","Clinical SAS · Accenture","Praveen sir is generous with feedback. He pushed me hard, and that's what got me hired."],
  ["Manish T.","Programmer I · Veeda","The pay-after-offer model is a trust signal. It means they believe in their own training."],
  ["Shilpa K.","Clinical Programmer · ICON","Best ROI of any course I've taken. Salary jump from ₹3.2L to ₹9L in 7 months."],
  ["Tejas G.","SAS Programmer · Indegene","Looking back, the only regret I have is not joining SASXL sooner."],
  ["Pranav N.","Clinical SAS · Novotech","Capstone project portfolio is what got me through the technical interview round."],
  ["Yamini S.","Programmer Trainee · Cytel","Coming from MBA background, Clinical SAS felt alien. Praveen sir made it click in week 2."],
  ["Rakesh V.","Clinical SAS · IQVIA","Live coding sessions on real CDISC datasets — that's the only way to actually learn this stuff."],
  ["Pooja M.","Programmer I · Parexel","SASXL's interview prep is 10x more rigorous than what I'd faced in actual interviews."],
  ["Ashwin B.","Stat Programmer · Cytel","Best part: Praveen sir replies to WhatsApp queries even months after course completion."],
  ["Ruchi J.","Clinical Programmer · Cognizant","The macro library we built in module 3 is something I still use daily at my job."],
  ["Naveen R.","SAS Programmer · ICON","Pinnacle 21 validation training was hands-on. Day-one productivity at my new role."],
  ["Swati G.","Clinical Programmer · IQVIA","I came in scared of SAS macros. I left writing dynamic macro generators. SASXL works."],
  ["Aniket P.","Stat Programmer · Syneos","The 30-day money-back style placement promise made the decision easy. They delivered."],
  ["Bhavana K.","SAS Programmer · TCS","No coding experience to landing TCS in 6 months. The career roadmap was followed exactly."],
  ["Sandeep L.","Clinical SAS · Indegene","Three things that work: live cohort, 1:1 mentors, real capstone projects. SASXL has all three."],
  ["Madhuri T.","Programmer I · Veeda CR","Praveen sir's industry connections got my resume in front of recruiters. That's invaluable."],
];

function HomePage({ pushToast, openLead }) {
  return (
    <>
      {/* HERO with side lead form */}
      <section className="hero grid-bg noise" data-testid="hero-section">
        <div className="hero-bg"></div>
        <div className="container hero-content reveal" style={{ maxWidth: "1280px" }}>
          <div className="hero-split">
            <div className="hero-copy">
              <span className="eyebrow" data-testid="hero-eyebrow"><span className="pulse-dot"></span> Live Cohort Enrolling — Next Batch</span>
              <h1>Master <span className="grad">Clinical SAS</span><br />in a Tech-Driven Era.</h1>
              <p className="lead">End-to-end SDTM &amp; ADaM training, hands-on CDISC pipelines, and real-world pharma analytics — taught by industry veterans with 12+ years of submission experience.</p>
              <div className="hero-actions">
                <button className="btn btn-video" data-testid="hero-cta-demo" onClick={() => document.getElementById("demo-section")?.scrollIntoView({ behavior: "smooth" })}>
                  <i className="fa-solid fa-play"></i> Watch Free Demo Class
                </button>
                <button className="btn btn-whatsapp" data-testid="hero-cta-livedemo" onClick={() => openWA("Hi SASXL! I'd like to book a free LIVE demo class.")}>
                  <i className="fa-brands fa-whatsapp"></i> Book Free Live Demo
                </button>
              </div>
            </div>

            <HeroLeadForm pushToast={pushToast} />
          </div>

          <div className="hero-stats" data-testid="hero-stats" style={{ marginTop: "3rem" }}>
            <div className="stat"><div className="num">17+</div><div className="lbl">Years Training</div></div>
            <div className="stat"><div className="num">5000+</div><div className="lbl">Students Trained</div></div>
            <div className="stat"><div className="num">1500+</div><div className="lbl">Placed in Pharma</div></div>
            <div className="stat"><div className="num">1:1</div><div className="lbl">Live Mentorship</div></div>
          </div>
        </div>
      </section>

      {/* DEMO VIDEO */}
      <section id="demo-section" data-testid="demo-section">
        <div className="container">
          <div className="section-head reveal text-center" style={{ marginInline: "auto" }}>
            <span className="eyebrow"><i className="fa-solid fa-circle-play"></i> Watch the Showcase</span>
            <h2 style={{ marginTop: "1rem" }}>45 seconds. <span className="grad">8 reasons</span> to choose SASXL.</h2>
            <p>An auto-playing animated tour of why students transition careers with us.</p>
          </div>
          <CinemaShowcase />
        </div>
      </section>

      {/* TRAINER PROFILE */}
      <section style={{ background: "var(--bg-surface)" }} data-testid="trainer-section">
        <div className="container">
          <div className="section-head reveal">
            <span className="eyebrow"><i className="fa-solid fa-user-tie"></i> Lead Trainer</span>
            <h2 style={{ marginTop: "1rem" }}>Learn from a <span className="grad">CDISC veteran</span>, not a course recorder.</h2>
          </div>
          <div className="trainer-hero reveal" data-testid="trainer-card">
            <div className="trainer-photo" style={{ backgroundImage: "url('https://customer-assets.emergentagent.com/job_sas-course-check/artifacts/pqcxeexr_Praveen%20Tirupuram.png')" }} aria-label="Praveen Tirupuram — Lead Trainer"></div>
            <div>
              <h3 style={{ fontSize: "1.6rem" }}>Praveen Tirupuram</h3>
              <div className="trainer-meta">
                <span className="pill">12+ yrs experience</span>
                <span className="pill">CDISC SDTM SME</span>
                <span className="pill">Corporate Trainer</span>
                <span className="pill">Career Mentor</span>
              </div>
              <p style={{ color: "var(--text-secondary)", margin: 0 }}>
                Praveen is the founder of SASXL and the lead trainer for every cohort. He brings 12+ years of pharma submission experience and has personally trained and placed 1500+ professionals into Clinical SAS roles. He runs every weekend cohort live — no recorded substitute, no junior assistants.
              </p>
              <div className="trainer-stats">
                <div className="ts"><div className="n">12+</div><div className="l">Years</div></div>
                <div className="ts"><div className="n">30+</div><div className="l">Submissions</div></div>
                <div className="ts"><div className="n">1500+</div><div className="l">Mentees</div></div>
              </div>
              <p style={{ marginTop: "1.25rem", fontSize: "0.85rem", color: "var(--text-muted)" }}>
                <i className="fa-brands fa-linkedin"></i> LinkedIn profile coming soon.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* 6-MONTH ROADMAP */}
      <section data-testid="roadmap-6m-section">
        <div className="container">
          <div className="section-head reveal">
            <span className="eyebrow"><i className="fa-solid fa-route"></i> Career Transformation</span>
            <h2 style={{ marginTop: "1rem" }}>Your <span className="grad">6-Month</span> Roadmap to a Job Offer.</h2>
            <p>Six dedicated months. Same world-class curriculum, compressed and intensified. Here's the exact monthly plan we execute with you — culminating in your offer letter.</p>
          </div>
          <div className="phase-grid reveal" data-testid="phase-grid">
            {PHASES_6.map((m, i) => (
              <div className="phase-card" key={i} data-testid={`phase-card-${i+1}`}>
                <div className="phase-num">0{i+1}</div>
                <div className="phase-body">
                  <div className="phase-tag">{m.m}</div>
                  <h4>{m.t}</h4>
                  <p>{m.d}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PLACEMENT FEE BANNER */}
      <section style={{ paddingTop: 0, paddingBottom: "4rem" }} data-testid="placement-fee-banner">
        <div className="container">
          <div className="fee-banner reveal">
            <div className="fee-banner-left">
              <span className="eyebrow"><i className="fa-solid fa-handshake"></i> Placement-Linked Fee</span>
              <h3>Pay Majority of Fees <span className="grad">After</span> Receiving Job Offer.</h3>
              <p>25% locks your seat. The remaining 75% is paid only after your Offer Letter is in hand. Your success — and ours — are aligned.</p>
            </div>
            <div className="fee-banner-right">
              <div className="fee-pill"><span className="big">25%</span><span className="lbl">At Enrollment</span></div>
              <div className="fee-arrow"><i className="fa-solid fa-arrow-right"></i></div>
              <div className="fee-pill featured"><span className="big">75%</span><span className="lbl">After Job Offer</span></div>
              <button className="btn btn-whatsapp" style={{ marginTop: "1rem" }} data-testid="fee-banner-cta" onClick={() => openWA("Hi SASXL! Please share details about the placement-linked fee structure.")}>
                <i className="fa-brands fa-whatsapp"></i> Learn More
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* WORKFLOW */}
      <section style={{ background: "var(--bg-surface)" }} data-testid="workflow-section">
        <div className="container">
          <div className="section-head reveal">
            <span className="eyebrow"><i className="fa-solid fa-diagram-project"></i> Workflow Simulation</span>
            <h2 style={{ marginTop: "1rem" }}>From Raw Data to <span className="grad">Regulatory Submission</span></h2>
            <p>Follow the exact pipeline a Clinical SAS Programmer executes — from raw EDC capture to CSR-ready outputs.</p>
          </div>
          <div className="workflow reveal" data-testid="workflow-pipeline">
            {[
              ["1","RAW_EDC","Capture","Subject & visit data ingested from EDC systems."],
              ["2","SDTM","Standardize","Map to AE, CM, DM, EX, VS datasets per CDISC."],
              ["3","ADaM","Analyze","Build ADSL, ADAE, ADTTE for statistical analysis."],
              ["4","TLF","Tabulate","Generate tables, listings & figures for CSR."],
              ["5","SUBMIT","eCTD","FDA / EMA submission-ready packages."],
            ].map(([n, tag, h, p]) => (
              <div className="workflow-node" key={n}>
                <span className="step-num">{n}</span>
                <span className="tag">{tag}</span>
                <h4>{h}</h4>
                <p>{p}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section data-testid="testimonials-section">
        <div className="container">
          <div className="section-head reveal">
            <span className="eyebrow"><i className="fa-solid fa-quote-left"></i> Learner Voices</span>
            <h2 style={{ marginTop: "1rem" }}>40+ Success Stories from the <span className="grad">SASXL Cohort</span>.</h2>
            <p style={{ fontSize: "0.85rem", color: "var(--text-muted)" }}>Names anonymized at learner request. Avatars are AI-illustrated to protect identity.</p>
          </div>
        </div>
        <div className="test-marquee reveal" data-testid="testimonials-marquee">
          <div className="test-track">
            {[...TESTIMONIALS, ...TESTIMONIALS].map(([name, role, q], i) => (
              <div className="test test-card" key={i} data-testid={`testimonial-${i}`}>
                <div className="test-stars" aria-label="5 stars">★★★★★</div>
                <p>"{q}"</p>
                <div className="who">
                  <img className="av-img" src={avatar(name)} alt={name} loading="lazy" />
                  <div className="meta"><strong>{name}</strong><span>{role}</span></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* COMPARISON TABLE — SASXL vs Others */}
      <section style={{ background: "var(--bg-surface)" }} data-testid="comparison-section">
        <div className="container">
          <div className="section-head reveal">
            <span className="eyebrow"><i className="fa-solid fa-scale-balanced"></i> Why SASXL</span>
            <h2 style={{ marginTop: "1rem" }}>SASXL vs <span className="grad">Other Institutes</span>.</h2>
            <p>Side-by-side, no fluff. Here's what you get with SASXL — and what you usually don't get elsewhere.</p>
          </div>
          <div className="compare-wrap reveal">
            <table className="compare-table" data-testid="compare-table">
              <thead><tr><th>Feature</th><th className="hot">SASXL</th><th>Other Institutes</th></tr></thead>
              <tbody>
                {COMPARE_ROWS.map(([feat, sxl, others], i) => (
                  <tr key={i}>
                    <td><strong>{feat}</strong></td>
                    <td className="hot"><i className="fa-solid fa-check-circle"></i> {sxl === true ? "Yes" : sxl}</td>
                    <td className="dim"><i className="fa-solid fa-circle-xmark"></i> {others}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* FREE RESOURCES */}
      <section data-testid="resources-section">
        <div className="container">
          <div className="section-head reveal">
            <span className="eyebrow"><i className="fa-solid fa-download"></i> Free Downloads</span>
            <h2 style={{ marginTop: "1rem" }}>Free <span className="grad">Career Resources</span> — instant access.</h2>
            <p>No payment. We just need your email so we can send the PDF + occasional career tips. Unsubscribe anytime.</p>
          </div>
          <div className="resource-grid reveal">
            {RESOURCES.map((r) => (
              <div className="resource-card" key={r.id} data-testid={`resource-${r.id}`}>
                <div className="resource-icon"><i className={`fa-solid ${r.icon}`}></i></div>
                <span className="resource-tag">{r.tag}</span>
                <h3>{r.title}</h3>
                <p>{r.desc}</p>
                <button className="btn btn-primary" style={{ width: "100%", justifyContent: "center", marginTop: "1rem" }} data-testid={`resource-cta-${r.id}`}
                  onClick={() => openLead(`Resource: ${r.title}`)}>
                  <i className="fa-solid fa-download"></i> Get Free PDF
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* GOOGLE REVIEWS */}
      <section style={{ background: "var(--bg-surface)" }} data-testid="google-reviews-section">
        <div className="container">
          <div className="section-head reveal">
            <span className="eyebrow"><i className="fa-brands fa-google"></i> Google Reviews</span>
            <h2 style={{ marginTop: "1rem" }}><span className="grad">4.9 / 5</span> from our learners.</h2>
            <p style={{ fontSize: "0.85rem", color: "var(--text-muted)" }}>Verified Google reviews. (Live Google embed will be wired up once we hit 50+ reviews on our Business profile.)</p>
          </div>
          <div className="review-grid reveal">
            {GOOGLE_REVIEWS.map(([name, stars, q], i) => (
              <div className="review-card" key={i} data-testid={`google-review-${i}`}>
                <div className="rev-head">
                  <img className="av-img" src={avatar(name + "g")} alt={name} loading="lazy" />
                  <div>
                    <strong>{name}</strong>
                    <div className="rev-stars">{"★".repeat(stars)}<span className="rev-platform"> · Google</span></div>
                  </div>
                </div>
                <p>"{q}"</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PLACEMENT DISCLAIMER */}
      <section style={{ paddingTop: 0, paddingBottom: "3rem" }} data-testid="placement-disclaimer">
        <div className="container">
          <div className="disclaimer reveal">
            <i className="fa-solid fa-circle-info"></i>
            <p><strong>Placement Disclaimer:</strong> SASXL provides comprehensive placement assistance — resume building, mock interviews, referral introductions, and interview preparation. Final job offers depend on individual candidate skills, interview performance, market conditions, and employer requirements. We do not guarantee employment, but we do guarantee our effort.</p>
          </div>
        </div>
      </section>

      {/* CTA STRIP */}
      <section style={{ padding: "4rem 0" }} data-testid="cta-strip">
        <div className="container">
          <div className="form-shell text-center reveal" style={{ background: "linear-gradient(135deg, rgba(0, 240, 255, 0.08), rgba(177, 78, 255, 0.06))", borderColor: "var(--border-active)" }}>
            <h2 style={{ fontSize: "clamp(1.6rem, 3vw, 2.3rem)", marginBottom: "0.75rem" }}>Ready to transition into <span className="grad">Clinical SAS</span>?</h2>
            <p style={{ color: "var(--text-secondary)", maxWidth: 580, margin: "0 auto 1.75rem" }}>Get a personalized 6-month roadmap on a 30-min call with our placement team.</p>
            <button className="btn btn-whatsapp" data-testid="strip-cta-whatsapp" onClick={() => openWA("Hi! I'd like to book a free demo class for Clinical SAS training.")}>
              <i className="fa-brands fa-whatsapp"></i> Book Free Demo on WhatsApp
            </button>
          </div>
        </div>
      </section>
    </>
  );
}

// ============ COURSES ============
const MODULES = [
  ["M_01","Foundations of Clinical Research",["ICH-GCP, FDA & EMA frameworks","Trial phases & study designs","Stakeholders: Sponsor, CRO, Site"]],
  ["M_02","Base SAS Programming",["DATA step mechanics","PROC SQL, MERGE, JOIN","Functions, formats, informats"]],
  ["M_03","Advanced SAS",["SAS Macros & macro variables","%DO loops, conditional logic","Reusable utility libraries"]],
  ["M_04","CDISC Fundamentals",["SDTM & ADaM standards","Define-XML, ADRG, SDRG","Controlled Terminology"]],
  ["M_05","SDTM Domain Mapping",["DM, AE, CM, EX, VS, LB domains","Trial Design datasets (TI, TA, TV)","SUPPQUAL & RELREC mapping"]],
  ["M_06","ADaM Datasets",["ADSL — subject-level analysis","ADAE, ADTTE, ADLB","Imputation flags & traceability"]],
  ["M_07","TLF — Tables, Listings, Figures",["PROC REPORT, PROC TABULATE","Kaplan-Meier & forest plots","RTF outputs for CSR"]],
  ["M_08","Statistical Programming",["Descriptive & inferential stats","PROC MEANS, FREQ, LIFETEST","Survival & efficacy analysis"]],
  ["M_09","Define-XML & Submission",["Define-XML 2.0 / 2.1 authoring","Pinnacle 21 validation","eCTD submission package"]],
  ["M_10","QC & Double Programming",["Independent QC techniques","PROC COMPARE workflows","SOPs & audit trails"]],
  ["M_11","SAS in Pharmacovigilance",["Adverse event coding (MedDRA)","Safety database integration","DSUR/PSUR analytics"]],
  ["M_12","SAS + R Integration",["R fundamentals for biostatisticians","SAS↔R data exchange","ggplot2 & survival in R for pharma"]],
  ["M_13","AI Automation in Clinical SAS",["GenAI-assisted SDTM mapping","LLM-powered QC & double-prog","Prompt engineering for pharma analytics"]],
  ["M_14","Capstone Project I",["End-to-end Phase III oncology study","SDTM → ADaM → TLF pipeline","Mock FDA submission package"]],
  ["M_15","Capstone Project II",["Diabetes trial — efficacy & safety","Define-XML + ADRG authoring","Pinnacle 21 validation report"]],
  ["M_16","Interview & Placement",["Mock interviews with hiring partners","Resume & LinkedIn rebuild","Salary negotiation playbook"]],
];

const FAQS = [
  ["Do I need a coding background?","No. The course starts with Base SAS from scratch. Pharma background helps but is not mandatory."],
  ["Is the certificate industry-recognized?","Our certificate is endorsed by mentors actively working in IQVIA, Cognizant, Parexel, and Novartis. We do not currently maintain formal corporate partnerships — we are building this network."],
  ["What is the placement support model?","The Career Launch Academy with Internship program includes internship, resume preparation, mock interviews, technical &amp; HR interview preparation, and the Eligible Fresher Career Pathway. Full program terms apply."],
  ["Do you offer EMI?","Self-Paced (₹9,999) and Live + Mentorship (₹39,999) are one-time program fees. Career Launch Academy has a two-part structure: ₹21,250 pay-to-start plus ₹63,750 deferred balance payable after a qualifying offer letter, per the signed program agreement."],
  ["Can I watch a free demo?","Absolutely. Use the 'Watch Free Demo Class' button on the homepage, or WhatsApp us to book a live 60-minute demo."],
];

function PayModal({ open, plan, onClose }) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState("form"); // form | success | failed
  const [errMsg, setErrMsg] = useState("");

  useEffect(() => {
    if (open) {
      setStatus("form"); setErrMsg(""); setBusy(false);
    }
  }, [open, plan]);

  if (!open || !plan) return null;

  const openCheckout = async () => {
    if (busy) return; // prevent double-click
    if (!name.trim() || !email.trim() || !phone.trim()) {
      setErrMsg("Please fill Name, Email and Phone."); return;
    }
    setBusy(true); setErrMsg("");
    try {
      const r = await axios.post(`${API}/payments/create-order`, {
        course_id: plan.course_id,
        student_name: name.trim(),
        email: email.trim(),
        phone: phone.trim(),
      });
      const { order_id, amount, currency, key_id, course_name } = r.data;
      if (!window.Razorpay) {
        setBusy(false); setStatus("failed");
        setErrMsg("Payment library not loaded. Please refresh and try again.");
        return;
      }
      const rzp = new window.Razorpay({
        key: key_id, // public test key id only
        order_id, amount, currency,
        name: "SASXL",
        description: course_name,
        prefill: { name: name.trim(), email: email.trim(), contact: phone.trim() },
        theme: { color: "#00d4b8" },
        handler: async (resp) => {
          try {
            await axios.post(`${API}/payments/verify`, {
              razorpay_order_id: resp.razorpay_order_id,
              razorpay_payment_id: resp.razorpay_payment_id,
              razorpay_signature: resp.razorpay_signature,
            });
            setStatus("success");
          } catch {
            setStatus("failed");
            setErrMsg("Payment received but verification failed. Our team will contact you.");
          } finally { setBusy(false); }
        },
        modal: {
          ondismiss: () => { setBusy(false); setStatus("failed"); setErrMsg("Payment cancelled."); },
        },
      });
      rzp.on("payment.failed", (e) => {
        setBusy(false); setStatus("failed");
        setErrMsg(e?.error?.description || "Payment failed. Please retry.");
      });
      rzp.open();
    } catch (e) {
      setBusy(false); setStatus("failed");
      setErrMsg(e?.response?.data?.detail === "PaymentsNotConfigured"
        ? "Payments are temporarily unavailable. Please contact us on WhatsApp."
        : "Unable to start payment. Please retry or contact us on WhatsApp.");
    }
  };

  const waMsg = `Hi SASXL! I'd like help enrolling in ${plan.course_name} (${plan.display_amount}).`;

  return (
    <div className="modal-backdrop" onClick={(e) => e.target === e.currentTarget && onClose()} data-testid="pay-modal-backdrop">
      <div className="modal-shell" data-testid="pay-modal">
        <button className="modal-close" onClick={onClose} aria-label="Close" data-testid="pay-modal-close">×</button>
        {status === "form" && (<>
          <span className="eyebrow"><i className="fa-solid fa-lock"></i> Secure Test Checkout</span>
          <h2 style={{ fontSize: "1.5rem", margin: "0.8rem 0 0.4rem" }}>{plan.course_name}</h2>
          <p style={{ color: "var(--text-secondary)", margin: "0 0 1.2rem", fontSize: "0.92rem" }}>
            You will be charged <strong>{plan.display_amount}</strong> on Razorpay (test mode).
            {plan.course_id === "career_launch_initial" && (
              <> The remaining <strong>₹63,750</strong> is deferred — <strong>not</strong> auto-debited.</>
            )}
          </p>
          <div className="field" style={{ marginBottom: "0.8rem" }}>
            <label>Full Name *</label>
            <input type="text" data-testid="pay-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Anita Verma" />
          </div>
          <div className="field" style={{ marginBottom: "0.8rem" }}>
            <label>Email *</label>
            <input type="email" data-testid="pay-email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" />
          </div>
          <div className="field" style={{ marginBottom: "0.8rem" }}>
            <label>Phone (WhatsApp) *</label>
            <input type="tel" data-testid="pay-phone" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+91 98XXXXXXXX" />
          </div>
          {errMsg && <p style={{ color: "#ff6b6b", fontSize: "0.85rem", margin: "0.4rem 0" }} data-testid="pay-error">{errMsg}</p>}
          <button className="btn btn-primary" style={{ width: "100%", justifyContent: "center", marginTop: "0.5rem" }}
                  onClick={openCheckout} disabled={busy} data-testid="pay-submit">
            <i className="fa-solid fa-shield-halved"></i> {busy ? "Opening secure checkout…" : `Continue to Pay ${plan.display_amount}`}
          </button>
          <button className="btn btn-whatsapp" style={{ width: "100%", justifyContent: "center", marginTop: "0.6rem", fontSize: "0.85rem" }}
                  onClick={() => openWA(waMsg)} data-testid="pay-whatsapp-fallback">
            <i className="fa-brands fa-whatsapp"></i> Or ask on WhatsApp
          </button>
        </>)}
        {status === "success" && (
          <div data-testid="pay-success" style={{ textAlign: "center" }}>
            <div style={{ fontSize: "3rem", color: "var(--brand-primary)" }}><i className="fa-solid fa-circle-check"></i></div>
            <h2 style={{ margin: "0.6rem 0", fontSize: "1.4rem" }}>Payment received successfully.</h2>
            <p style={{ color: "var(--text-secondary)" }}>Thank you for choosing SASXL. Our team will contact you with the next steps.</p>
            <button className="btn btn-primary" style={{ width: "100%", justifyContent: "center", marginTop: "1rem" }} onClick={onClose} data-testid="pay-success-close">Done</button>
          </div>
        )}
        {status === "failed" && (
          <div data-testid="pay-failed" style={{ textAlign: "center" }}>
            <div style={{ fontSize: "3rem", color: "#ff6b6b" }}><i className="fa-solid fa-triangle-exclamation"></i></div>
            <h2 style={{ margin: "0.6rem 0", fontSize: "1.3rem" }}>{errMsg || "Payment not completed."}</h2>
            <p style={{ color: "var(--text-secondary)" }}>You can retry the payment or contact us on WhatsApp.</p>
            <button className="btn btn-primary" style={{ width: "100%", justifyContent: "center", marginTop: "0.8rem" }} onClick={() => { setStatus("form"); setErrMsg(""); }} data-testid="pay-retry">Retry Payment</button>
            <button className="btn btn-whatsapp" style={{ width: "100%", justifyContent: "center", marginTop: "0.5rem" }} onClick={() => openWA(waMsg)} data-testid="pay-failed-whatsapp">
              <i className="fa-brands fa-whatsapp"></i> Contact on WhatsApp
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function CoursesPage() {
  const [openIdx, setOpenIdx] = useState(-1);
  const [payPlan, setPayPlan] = useState(null);
  const openPay = (plan) => setPayPlan(plan);
  return (
    <>
      <section className="page-hero grid-bg noise"><div className="container reveal">
        <span className="eyebrow"><i className="fa-solid fa-book"></i> Curriculum · 6 Months</span>
        <h1>The Complete <span className="grad">Clinical SAS</span> Curriculum.</h1>
        <p>14 modules. 80+ hours of live mentorship. 4 capstone projects. Built around the real-world pharma submission lifecycle.</p>
      </div></section>

      <section style={{ paddingTop: "2rem" }} data-testid="modules-section"><div className="container">
        <div className="modules reveal">
          {MODULES.map(([num, h, items]) => (
            <div className="module" key={num} data-testid={`module-${num}`}>
              <span className="num">{num}</span><h3>{h}</h3>
              <ul>{items.map((i) => <li key={i}>{i}</li>)}</ul>
            </div>
          ))}
        </div>
      </div></section>

      {/* Courses page bottom section — Career Launch Academy focused */}
      <section style={{ paddingBottom: 0 }} data-testid="courses-fee-banner"><div className="container">
        <div className="fee-banner reveal">
          <div className="fee-banner-left">
            <span className="eyebrow"><i className="fa-solid fa-graduation-cap"></i> Career Launch Academy</span>
            <h3><span className="grad">6-Month Career Launch Program</span> with Internship.</h3>
            <p style={{ marginTop: "0.5rem" }}><strong>Total Program Fee ₹85,000</strong> · includes Internship, 6-month training, project, resume &amp; interview preparation and the eligible fresher career pathway. The remaining ₹63,750 deferred balance becomes payable after receipt of a qualifying offer letter, subject to the signed program agreement and applicable program terms.</p>
          </div>
          <div className="fee-banner-right">
            <div className="fee-pill"><span className="big">₹21,250</span><span className="lbl">Pay to Start · 25%</span></div>
            <div className="fee-arrow"><i className="fa-solid fa-arrow-right"></i></div>
            <div className="fee-pill featured"><span className="big">₹63,750</span><span className="lbl">Deferred Balance · 75%</span></div>
            <button className="btn btn-whatsapp" style={{ marginTop: "1rem" }} data-testid="courses-fee-cta" onClick={() => openPay({ course_id: "career_launch_initial", course_name: "Career Launch Academy with Internship", display_amount: "₹21,250" })}>
              <i className="fa-solid fa-shield-halved"></i> Pay ₹21,250 & Apply
            </button>
          </div>
        </div>
      </div></section>

      {/* Bottom benefit strip */}
      <section style={{ paddingTop: "3rem", paddingBottom: "1rem" }} data-testid="benefit-strip"><div className="container">
        <div className="benefit-strip reveal">
          {[
            ["fa-industry", "Industry-Relevant Training"],
            ["fa-flask", "Practical Projects & Real-World Exposure"],
            ["fa-user-tie", "Mentorship & Personalized Guidance"],
            ["fa-file-lines", "Resume & Interview Preparation"],
            ["fa-briefcase", "Career Support for Your Success"],
          ].map(([ic, t], i) => (
            <div className="benefit-pill" key={i}><i className={`fa-solid ${ic}`}></i><span>{t}</span></div>
          ))}
        </div>
      </div></section>

      <section style={{ background: "var(--bg-surface)" }} data-testid="pricing-section"><div className="container">
        <div className="section-head reveal">
          <span className="eyebrow"><i className="fa-solid fa-bolt"></i> Programs &amp; Pricing</span>
          <h2 style={{ marginTop: "1rem" }}>SASXL <span className="grad">Programs &amp; Pricing</span>.</h2>
          <p>Three structured paths to build Clinical SAS career skills — choose the level of support that fits your learning goals.</p>
        </div>
        <div className="pricing-grid reveal">
          <div className="price-card" data-testid="plan-self-paced">
            <h3 style={{ fontSize: "1.15rem", color: "var(--text-secondary)" }}>Self-Paced</h3>
            <div style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: "0.72rem", letterSpacing: "0.1em", color: "var(--text-muted)", textTransform: "uppercase", marginBottom: "0.4rem" }}>Learn at Your Own Pace</div>
            <div className="price-tag"><span className="currency">₹</span>9,999</div>
            <div className="price-meta">One-time · Total program fee</div>
            <p style={{ color: "var(--text-secondary)", fontSize: "0.88rem", margin: "0 0 1rem" }}>Build your Clinical SAS foundation through structured self-paced learning, practical project exposure and interview preparation.</p>
            <ul>
              <li>Clinical SAS Training — SAS · SDTM · ADaM · TLFs · Clinical Workflows</li>
              <li>Self-Paced Video Recordings — flexible structured lessons</li>
              <li>Practical Clinical SAS Project — hands-on workflow exposure</li>
              <li>Interview Question Bank — SAS · Clinical SAS · SDTM · ADaM · TLFs</li>
            </ul>
            <ul className="price-notincl">
              <li>Live Classes</li>
              <li>R Programming</li>
              <li>Resume Training</li>
              <li>Employment Pathway</li>
            </ul>
            <button className="btn btn-ghost" style={{ width: "100%", justifyContent: "center" }} data-testid="plan-self-paced-cta" onClick={() => openPay({ course_id: "self_paced", course_name: "Self-Paced Clinical SAS", display_amount: "₹9,999" })}>START SELF-PACED</button>
            <button className="btn btn-whatsapp" style={{ width: "100%", justifyContent: "center", marginTop: "0.5rem", fontSize: "0.85rem" }} data-testid="plan-self-paced-wa" onClick={() => openWA("Hi SASXL! I'd like to know more about the Self-Paced program (₹9,999).")}>
              <i className="fa-brands fa-whatsapp"></i> Ask on WhatsApp
            </button>
          </div>

          <div className="price-card featured" data-testid="plan-live-mentorship">
            <span className="badge">MOST POPULAR</span>
            <h3 style={{ fontSize: "1.15rem", color: "var(--brand-primary)" }}>Live + Mentorship</h3>
            <div style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: "0.72rem", letterSpacing: "0.1em", color: "var(--brand-primary)", textTransform: "uppercase", marginBottom: "0.4rem" }}>Live Training + Personalized Support</div>
            <div className="price-tag"><span className="currency">₹</span>39,999</div>
            <div className="price-meta">Total program fee · Live cohort seat</div>
            <p style={{ color: "var(--text-secondary)", fontSize: "0.88rem", margin: "0 0 1rem" }}>Complete Clinical Programming learning path with live classes, mentorship, projects, resume preparation and interview support.</p>
            <ul>
              <li>Clinical SAS Training — SAS · SDTM · ADaM · TLFs · Clinical Workflows</li>
              <li>R Programming — Fundamentals · Data Analysis · Clinical Applications</li>
              <li>Clinical AI &amp; Automation — AI-assisted workflows · productivity · automation</li>
              <li>Practical Project — hands-on with validation exposure</li>
              <li>Resume Preparation — professional CV &amp; project representation</li>
              <li>Interview Preparation — Technical · Project-Based · HR</li>
            </ul>
            <button className="btn btn-primary" style={{ width: "100%", justifyContent: "center" }} data-testid="plan-live-mentorship-cta" onClick={() => openPay({ course_id: "live_mentorship", course_name: "Live + Mentorship", display_amount: "₹39,999" })}>JOIN LIVE + MENTORSHIP</button>
            <button className="btn btn-whatsapp" style={{ width: "100%", justifyContent: "center", marginTop: "0.5rem", fontSize: "0.85rem" }} data-testid="plan-live-mentorship-wa" onClick={() => openWA("Hi SASXL! I'd like to know more about Live + Mentorship (₹39,999).")}>
              <i className="fa-brands fa-whatsapp"></i> Ask on WhatsApp
            </button>
          </div>

          <div className="price-card cla" data-testid="plan-career-launch">
            <span className="badge" style={{ background: "var(--brand-secondary)", color: "#050814" }}>FRESHERS ONLY</span>
            <h3 style={{ fontSize: "1.15rem", color: "var(--brand-secondary)" }}>Career Launch Academy</h3>
            <div style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: "0.72rem", letterSpacing: "0.1em", color: "var(--brand-secondary)", textTransform: "uppercase", marginBottom: "0.5rem" }}>6-Month Career Launch Program <span style={{ color: "#fff" }}>WITH INTERNSHIP</span></div>

            <div className="cla-total">
              <span className="cla-total-lbl">TOTAL PROGRAM FEE</span>
              <span className="cla-total-amt">₹85,000</span>
              <span className="cla-total-note">Includes Internship</span>
            </div>

            <div className="cla-splits">
              <div className="cla-split">
                <span className="cla-split-lbl">PAY TO START · 25%</span>
                <span className="cla-split-amt">₹21,250</span>
                <span className="cla-split-note">Initial payment</span>
              </div>
              <div className="cla-split cla-split-deferred">
                <span className="cla-split-lbl">REMAINING BALANCE · 75%</span>
                <span className="cla-split-amt">₹63,750</span>
                <span className="cla-split-note">Deferred balance</span>
              </div>
            </div>

            <ul style={{ marginTop: "1.25rem" }}>
              <li>6-Month Intensive Career Program — instructor-led</li>
              <li>SAS Programming · R Programming · Clinical SAS</li>
              <li>SDTM · ADaM · TLF Programming</li>
              <li>Clinical AI &amp; Automation</li>
              <li>End-to-End Clinical SAS Project — Source Data → Data Review → SDTM → ADaM → TLFs → Validation → Deliverables</li>
              <li><strong style={{ color: "var(--brand-secondary)" }}>Internship</strong> — included program component</li>
              <li>Resume Preparation · Project Explanation Prep</li>
              <li>Technical Interview Prep · Mock Interview Guidance</li>
              <li>Career Preparation · Eligible Fresher Career Pathway</li>
            </ul>

            <button className="btn btn-ghost" style={{ width: "100%", justifyContent: "center", borderColor: "var(--brand-secondary)", color: "var(--brand-secondary)" }} data-testid="plan-career-launch-cta" onClick={() => openPay({ course_id: "career_launch_initial", course_name: "Career Launch Academy with Internship", display_amount: "₹21,250" })}>PAY ₹21,250 & APPLY</button>
            <button className="btn btn-whatsapp" style={{ width: "100%", justifyContent: "center", marginTop: "0.5rem", fontSize: "0.85rem" }} data-testid="plan-career-launch-wa" onClick={() => openWA("Hi SASXL! I'd like to know more about Career Launch Academy with Internship (₹85,000 total · ₹21,250 pay to start).")}>
              <i className="fa-brands fa-whatsapp"></i> Ask on WhatsApp
            </button>

            <p className="cla-terms">The remaining ₹63,750 deferred balance becomes payable after receipt of a qualifying offer letter, subject to the signed program agreement and applicable program terms.</p>
          </div>
        </div>

        <p className="pricing-disclaimer" data-testid="pricing-disclaimer">Eligibility criteria, attendance, assessments, project completion, participation and other terms &amp; conditions apply. Full program terms will be shared before enrollment.</p>
      </div></section>

      <section data-testid="faq-section"><div className="container" style={{ maxWidth: 880 }}>
        <div className="section-head reveal">
          <span className="eyebrow"><i className="fa-solid fa-circle-question"></i> FAQ</span>
          <h2 style={{ marginTop: "1rem" }}>Common <span className="grad">Questions</span>.</h2>
        </div>
        <div className="reveal">
          {FAQS.map(([q, a], i) => (
            <div className={`faq-item ${openIdx === i ? "open" : ""}`} key={i} data-testid={`faq-item-${i+1}`} onClick={() => setOpenIdx(openIdx === i ? -1 : i)}>
              <div className="q"><span>{q}</span><span className="toggle">{openIdx === i ? "−" : "+"}</span></div>
              <div className="a">{a}</div>
            </div>
          ))}
        </div>
      </div></section>
      <PayModal open={!!payPlan} plan={payPlan} onClose={() => setPayPlan(null)} />
    </>
  );
}

// ============ CAREER ============
const ROADMAP = [
  ["Year 0 · Onboarding","Trainee SAS Programmer","Learn the codebase. Shadow seniors. Execute QC on dummy datasets. Build internal SAS macro library."],
  ["Year 1–2 · Core Build","Clinical SAS Programmer","Own SDTM domain mapping. Independent ADaM development. Author Define-XML for small studies."],
  ["Year 3–5 · Senior Track","Senior SAS Programmer","Lead end-to-end study programming. Mentor juniors. Drive validation strategy."],
  ["Year 5–7 · Leadership","Lead / Principal Programmer","Architect cross-study standards. Author SOPs. Stakeholder management with sponsors."],
  ["Year 8–10 · Executive","Manager → Director of Stat Programming","Run programming portfolio. P&L responsibility. Vendor & resource strategy."],
];

function CareerPage() {
  return (
    <>
      <section className="page-hero grid-bg noise"><div className="container reveal">
        <span className="eyebrow"><i className="fa-solid fa-route"></i> 10-Year Trajectory</span>
        <h1>Your <span className="grad">Career Roadmap</span> in Clinical SAS.</h1>
        <p>From Trainee Programmer to Director of Biostatistics — here's the proven path our alumni follow.</p>
      </div></section>

      <section style={{ paddingTop: "2rem" }} data-testid="roadmap-section">
        <div className="container" style={{ maxWidth: 920 }}>
          <div className="roadmap reveal">
            {ROADMAP.map(([m, h, p]) => (
              <div className="roadmap-item" key={h}><span className="meta">{m}</span><h3>{h}</h3><p>{p}</p></div>
            ))}
          </div>
        </div>
      </section>

      <section style={{ background: "var(--bg-surface)" }} data-testid="salary-section"><div className="container">
        <div className="section-head reveal">
          <span className="eyebrow"><i className="fa-solid fa-money-bill-trend-up"></i> Salary Progression</span>
          <h2 style={{ marginTop: "1rem" }}>What You Earn <span className="grad">at Each Stage</span>.</h2>
          <p>Indicative compensation in INR (India market) — based on public hiring benchmarks, FY 2025.</p>
        </div>
        <div className="salary-grid reveal" data-testid="salary-india">
          {[["Trainee","₹4.5L","Year 0","c1"],["Programmer","₹7.5L","Year 1–2","c2"],["Senior","₹14L","Year 3–5","c3"],["Lead","₹24L","Year 5–7","c4"],["Director","₹45L+","Year 8+","c5"]].map(([r,a,y,c]) => (
            <div className={`salary-col ${c}`} key={r}><div className="role">{r}</div><div className="amount">{a}</div><div className="yrs">{y}</div></div>
          ))}
        </div>
        <div className="section-head reveal" style={{ marginTop: "4rem" }}>
          <h3 style={{ fontFamily: "Outfit", fontSize: "1.6rem" }}>Global Market (USD)</h3>
          <p>Onshore roles in the US, UK, and Switzerland command significantly higher compensation.</p>
        </div>
        <div className="salary-grid reveal" data-testid="salary-global">
          {[["Programmer I","$72K","Entry","c1"],["Programmer II","$95K","2–4 yrs","c2"],["Senior","$125K","5–7 yrs","c3"],["Principal","$165K","8–10 yrs","c4"],["Director","$220K+","10+ yrs","c5"]].map(([r,a,y,c]) => (
            <div className={`salary-col ${c}`} key={r}><div className="role">{r}</div><div className="amount">{a}</div><div className="yrs">{y}</div></div>
          ))}
        </div>
      </div></section>

      <section style={{ padding: "4rem 0" }} data-testid="cta-strip"><div className="container">
        <div className="form-shell text-center reveal" style={{ background: "linear-gradient(135deg, rgba(0, 240, 255, 0.08), rgba(177, 78, 255, 0.06))", borderColor: "var(--border-active)" }}>
          <h2 style={{ fontSize: "clamp(1.6rem, 3vw, 2.3rem)", marginBottom: "0.75rem" }}>Plan your <span className="grad">5-year jump</span>.</h2>
          <p style={{ color: "var(--text-secondary)", maxWidth: 580, margin: "0 auto 1.75rem" }}>Get a personalized career roadmap on a 30-min call with our placement team.</p>
          <button className="btn btn-whatsapp" data-testid="career-cta-whatsapp" onClick={() => openWA("Hi! I'd like a free career consultation for Clinical SAS.")}>
            <i className="fa-brands fa-whatsapp"></i> Book Career Consultation
          </button>
        </div>
      </div></section>
    </>
  );
}

// ============ PROJECTS ============
const PROJECTS = [
  ["onco","study/onco-phase3-pfs","Oncology — Phase III PFS Study","End-to-end SDTM & ADaM build for a Progression-Free Survival study (N=420). Kaplan-Meier + Cox PH analysis. Mock FDA eCTD submission.",["SDTM","ADTTE","LIFETEST","Define-XML"]],
  ["diabetes","study/diabetes-t2dm-efficacy","Diabetes — T2DM Efficacy & Safety","Long-term HbA1c reduction trial (N=850, 52 weeks). ADSL, ADLB, ADAE construction with imputation strategies and ADRG authoring.",["ADSL","ADLB","Imputation","ADRG"]],
  ["cardio","study/cardio-heart-failure","Cardiology — Heart Failure Outcomes","Composite endpoint analysis with hospitalization & mortality. Forest plot generation and subgroup analyses across 7 strata.",["Forest Plot","Subgroup","PROC REPORT"]],
  ["pv","study/pv-safety-database","Pharmacovigilance — Safety Database","Adverse event aggregation across 4 studies. MedDRA coding integration, PSUR-ready outputs, signal detection mock dashboard.",["PV","MedDRA","PSUR","Dashboards"]],
];

function ProjectsPage() {
  return (
    <>
      <section className="page-hero grid-bg noise"><div className="container reveal">
        <span className="eyebrow"><i className="fa-solid fa-folder-tree"></i> Capstone Repository</span>
        <h1>Build <span className="grad">4 Real-World</span> Pharma Projects.</h1>
        <p>Every project mirrors an actual sponsor study — complete with raw EDC exports, SAP, and mock FDA submission package.</p>
      </div></section>
      <section style={{ paddingTop: "2rem" }}><div className="container">
        <div className="project-grid reveal">
          {PROJECTS.map(([id, slug, h, p, tags]) => (
            <div className="project-card" key={id} data-testid={`project-${id}`}>
              <div className="head"><span className="dot"></span> <span>{slug}</span></div>
              <h3>{h}</h3><p>{p}</p>
              <div className="tags">{tags.map((t) => <span className="tag" key={t}>{t}</span>)}</div>
            </div>
          ))}
        </div>
      </div></section>
      <section style={{ padding: "4rem 0" }}><div className="container">
        <div className="form-shell text-center reveal" style={{ background: "linear-gradient(135deg, rgba(0, 240, 255, 0.08), rgba(177, 78, 255, 0.06))", borderColor: "var(--border-active)" }}>
          <h2 style={{ fontSize: "clamp(1.6rem, 3vw, 2.3rem)", marginBottom: "0.75rem" }}>See a <span className="grad">sample project</span> walkthrough.</h2>
          <p style={{ color: "var(--text-secondary)", maxWidth: 580, margin: "0 auto 1.75rem" }}>Request a 20-min mentor walkthrough of any capstone — code, datasets, and output included.</p>
          <button className="btn btn-whatsapp" data-testid="projects-cta-whatsapp" onClick={() => openWA("Hi! Please share a sample Clinical SAS project walkthrough.")}>
            <i className="fa-brands fa-whatsapp"></i> Request Project Walkthrough
          </button>
        </div>
      </div></section>
    </>
  );
}

// ============ MENTORS ============
const MENTORS = [
  ["aravind","Dr. Aravind Reddy","Lead SAS Programmer · 12 yrs","Ex-IQVIA & Cognizant Pharma. Led 30+ Phase II/III oncology & cardiology submissions. CDISC-certified.","https://images.unsplash.com/photo-1599837565318-67429bde7162?crop=entropy&cs=srgb&fm=jpg&q=85&w=900"],
  ["priya","Priya Sharma","Sr. Statistical Programmer · 9 yrs","Ex-Novartis. Specialist in ADaM design, survival analysis, and rare disease studies. Pinnacle 21 SME.","https://images.unsplash.com/photo-1551288049-bebda4e38f71?crop=entropy&cs=srgb&fm=jpg&q=85&w=900"],
  ["karthik","Karthik Iyer","Clinical Data Manager · 10 yrs","Ex-Parexel & Syneos Health. Bridges DM and Programming. Expert in EDC, CDASH, and database lock workflows.","https://images.unsplash.com/photo-1669707041081-dd4d51943041?crop=entropy&cs=srgb&fm=jpg&q=85&w=900"],
];

function MentorsPage() {
  return (
    <>
      <section className="page-hero grid-bg noise"><div className="container reveal">
        <span className="eyebrow"><i className="fa-solid fa-user-tie"></i> Industry Mentors</span>
        <h1>Learn from <span className="grad">Pharma Veterans</span>.</h1>
        <p>Every mentor has 9+ years of hands-on Clinical SAS experience. Book a 1:1 session directly on WhatsApp.</p>
      </div></section>
      <section style={{ paddingTop: "2rem" }}><div className="container">
        <div className="mentor-grid reveal">
          {MENTORS.map(([id, name, role, desc, img]) => (
            <div className="mentor-card" key={id} data-testid={`mentor-${id}`}>
              <div className="mentor-img" style={{ backgroundImage: `url('${img}')` }}></div>
              <div className="mentor-body">
                <h3>{name}</h3>
                <div className="mentor-role">{role}</div>
                <p>{desc}</p>
                <button className="btn btn-whatsapp" style={{ width: "100%", justifyContent: "center" }} data-testid={`book-mentor-${id}`} onClick={() => openWA(`Hi! I'd like to book a 1:1 session with ${name} for Clinical SAS training. Please share available time slots.`)}>
                  <i className="fa-brands fa-whatsapp"></i> Book a Session
                </button>
              </div>
            </div>
          ))}
        </div>
      </div></section>
      <section style={{ padding: "4rem 0" }}><div className="container">
        <div className="form-shell text-center reveal" style={{ background: "linear-gradient(135deg, rgba(0, 240, 255, 0.08), rgba(177, 78, 255, 0.06))", borderColor: "var(--border-active)" }}>
          <h2 style={{ fontSize: "clamp(1.6rem, 3vw, 2.3rem)", marginBottom: "0.75rem" }}>First mentor call is <span className="grad">free</span>.</h2>
          <button className="btn btn-whatsapp" data-testid="mentor-cta-whatsapp" onClick={() => openWA("Hi! I'd like to book my free mentor session.")}>
            <i className="fa-brands fa-whatsapp"></i> Book Free Mentor Call
          </button>
        </div>
      </div></section>
    </>
  );
}

// ============ CONTACT ============
function ContactPage({ pushToast }) {
  const [form, setForm] = useState({ name: "", email: "", phone: "", level: "", goal: "", message: "" });
  const [busy, setBusy] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone) {
      pushToast("Please fill in your name, email, and phone.", "err"); return;
    }
    setBusy(true);
    try {
      await axios.post(`${API}/leads`, { ...form, source: "contact-form" });
      pushToast("Saved! Opening WhatsApp with your details…");
      const text = [
        `Hi SASXL! I'd like to enroll / learn more.`, ``,
        `*Name:* ${form.name}`, `*Email:* ${form.email}`, `*Phone:* ${form.phone}`,
        `*Experience:* ${form.level || "Not specified"}`, `*Goal:* ${form.goal || "Not specified"}`, ``,
        `*Message:* ${form.message || "—"}`,
      ].join("\n");
      setTimeout(() => window.open(waUrl(text), "_blank", "noopener"), 600);
      setForm({ name: "", email: "", phone: "", level: "", goal: "", message: "" });
    } catch (err) {
      pushToast("Submission failed. Please WhatsApp us directly.", "err");
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <section className="page-hero grid-bg noise"><div className="container reveal">
        <span className="eyebrow"><i className="fa-solid fa-envelope"></i> Get In Touch</span>
        <h1>Let's <span className="grad">Connect</span>.</h1>
        <p>Submit your details — we'll save them and chat with you on WhatsApp instantly. No leads lost.</p>
      </div></section>

      <section style={{ paddingTop: "2rem" }}><div className="container" style={{ maxWidth: 1080 }}>
        <div className="reveal" style={{ display: "grid", gridTemplateColumns: "1.4fr 1fr", gap: "2rem" }} data-testid="contact-grid">
          <form className="form-shell" data-testid="contact-form" onSubmit={submit}>
            <h2 style={{ fontSize: "1.6rem", marginBottom: "0.4rem" }}>Tell us about you.</h2>
            <p style={{ color: "var(--text-secondary)", margin: "0 0 2rem" }}>Your details are stored securely and shared on WhatsApp for instant reply.</p>
            <div className="form-row">
              <div className="field"><label>Full Name *</label><input type="text" required value={form.name} onChange={set("name")} data-testid="input-name" placeholder="e.g. Anita Verma" /></div>
              <div className="field"><label>Phone (with country code) *</label><input type="tel" required value={form.phone} onChange={set("phone")} data-testid="input-phone" placeholder="e.g. +91 98XXXXXXXX" /></div>
            </div>
            <div className="field"><label>Email *</label><input type="email" required value={form.email} onChange={set("email")} data-testid="input-email" placeholder="anita@example.com" /></div>
            <div className="form-row">
              <div className="field"><label>Current Experience</label>
                <select value={form.level} onChange={set("level")} data-testid="select-level">
                  <option value="">Select…</option>
                  <option>Fresher / Student</option>
                  <option>0–2 years (non-pharma)</option>
                  <option>0–2 years (pharma background)</option>
                  <option>2–5 years (working professional)</option>
                  <option>5+ years (career switch)</option>
                </select>
              </div>
              <div className="field"><label>What's your goal?</label>
                <select value={form.goal} onChange={set("goal")} data-testid="select-goal">
                  <option value="">Select…</option>
                  <option>Land first Clinical SAS job</option>
                  <option>Switch from non-pharma role</option>
                  <option>Upskill in CDISC / SDTM / ADaM</option>
                  <option>Onshore (US/UK) move</option>
                  <option>Just exploring</option>
                </select>
              </div>
            </div>
            <div className="field"><label>Your Message</label><textarea value={form.message} onChange={set("message")} data-testid="input-message" placeholder="Tell us anything specific…"></textarea></div>
            <button type="submit" className="btn btn-whatsapp" style={{ width: "100%", justifyContent: "center" }} data-testid="contact-submit" disabled={busy}>
              <i className="fa-brands fa-whatsapp"></i> {busy ? "Saving…" : "Submit & Continue on WhatsApp"}
            </button>
            <p style={{ textAlign: "center", color: "var(--text-muted)", fontSize: "0.82rem", marginTop: "1rem" }}>Your information is stored in our CRM and shared via WhatsApp for instant reply.</p>
          </form>

          <div style={{ display: "flex", flexDirection: "column", gap: "1.25rem" }}>
            <div className="card"><div className="icon-wrap"><i className="fa-brands fa-whatsapp"></i></div>
              <h3 style={{ fontSize: "1.1rem" }}>WhatsApp</h3>
              <p className="mono" style={{ color: "var(--brand-primary)", margin: "0.5rem 0" }}>+91 8008 079 304</p>
              <p style={{ color: "var(--text-secondary)", fontSize: "0.92rem", margin: "0 0 1rem" }}>Fastest way to reach us. Replies within an hour.</p>
              <button className="btn btn-whatsapp" style={{ width: "100%", justifyContent: "center" }} onClick={() => openWA("Hi SASXL!")}>Chat Now</button>
            </div>
            <div className="card"><div className="icon-wrap"><i className="fa-solid fa-envelope"></i></div>
              <h3 style={{ fontSize: "1.1rem" }}>Email</h3>
              <p className="mono" style={{ color: "var(--brand-primary)", margin: "0.5rem 0" }}>hello@sasxl.com</p>
              <p style={{ color: "var(--text-secondary)", fontSize: "0.92rem", margin: 0 }}>For corporate &amp; partnership queries.</p>
            </div>
            <div className="card"><div className="icon-wrap"><i className="fa-solid fa-clock"></i></div>
              <h3 style={{ fontSize: "1.1rem" }}>Counselor Hours</h3>
              <p style={{ color: "var(--text-secondary)", fontSize: "0.92rem", margin: "0.5rem 0 0" }}>Mon – Sat · 9:00 AM – 9:00 PM IST<br />Sun · 10:00 AM – 6:00 PM IST</p>
            </div>
          </div>
        </div>
      </div></section>
    </>
  );
}

// ============ ADMIN ============
const STATUS_LABELS = {
  new: "New",
  contacted: "Contacted",
  roadmap_sent: "Roadmap Sent",
  follow_up: "Follow-up",
  converted: "Converted",
  closed: "Closed",
  // Legacy — kept read-only
  enrolled: "Enrolled (legacy)",
  dropped: "Dropped (legacy)",
};
const STATUS_KEYS = ["new", "contacted", "roadmap_sent", "follow_up", "converted", "closed"];

function AdminPage({ pushToast }) {
  const [token, setToken] = useState(localStorage.getItem("admin_token") || "");
  const [input, setInput] = useState("");
  const [tab, setTab] = useState("leads"); // leads | payments
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [viewLead, setViewLead] = useState(null);

  const load = async (tk) => {
    setLoading(true);
    try {
      const [a, b] = await Promise.all([
        axios.get(`${API}/admin/leads`, { headers: { "X-Admin-Token": tk } }),
        axios.get(`${API}/admin/stats`, { headers: { "X-Admin-Token": tk } }),
      ]);
      setLeads(a.data.leads || []); setStats(b.data);
    } catch (e) {
      pushToast("Invalid token or server error", "err");
      setToken(""); localStorage.removeItem("admin_token");
    } finally { setLoading(false); }
  };

  useEffect(() => { if (token) load(token); }, [token]);

  const login = (e) => {
    e.preventDefault();
    localStorage.setItem("admin_token", input);
    setToken(input);
  };

  const setStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/admin/leads/${id}?status=${status}`, {}, { headers: { "X-Admin-Token": token } });
      load(token);
    } catch { pushToast("Failed to update", "err"); }
  };

  const saveNotes = async (id, notes) => {
    try {
      await axios.patch(`${API}/admin/leads/${id}/notes`, { notes }, { headers: { "X-Admin-Token": token, "Content-Type": "application/json" } });
      pushToast("Notes saved");
      // Optimistic update of the viewed lead + full list
      setViewLead((v) => v && v.id === id ? { ...v, admin_notes: notes } : v);
      setLeads((ls) => ls.map((l) => l.id === id ? { ...l, admin_notes: notes } : l));
    } catch { pushToast("Failed to save notes", "err"); }
  };

  const filtered = leads.filter((l) => {
    if (statusFilter !== "all" && l.status !== statusFilter) return false;
    if (!search.trim()) return true;
    const q = search.trim().toLowerCase();
    return (
      (l.name || "").toLowerCase().includes(q) ||
      (l.email || "").toLowerCase().includes(q) ||
      (l.phone || "").toLowerCase().includes(q) ||
      (l.goal || "").toLowerCase().includes(q)
    );
  });

  if (!token) {
    return (
      <section className="page-hero grid-bg"><div className="container reveal" style={{ maxWidth: 460 }}>
        <span className="eyebrow"><i className="fa-solid fa-lock"></i> Admin</span>
        <h1>Admin <span className="grad">Login</span></h1>
        <form onSubmit={login} className="form-shell" style={{ marginTop: "2rem" }}>
          <div className="field"><label>Admin Token</label>
            <input type="password" value={input} onChange={(e) => setInput(e.target.value)} data-testid="admin-token-input" placeholder="Enter token" autoFocus />
          </div>
          <button type="submit" className="btn btn-primary" style={{ width: "100%", justifyContent: "center" }} data-testid="admin-login-btn">Sign In</button>
          <p style={{ fontSize: "0.78rem", color: "var(--text-muted)", marginTop: "1rem", textAlign: "center" }}>Token is stored in your browser only.</p>
        </form>
      </div></section>
    );
  }

  return (
    <section style={{ paddingTop: "7rem" }}><div className="container">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "1rem", marginBottom: "1.25rem" }}>
        <div><span className="eyebrow"><i className="fa-solid fa-chart-line"></i> CRM</span><h1 style={{ marginTop: "0.5rem" }}>Admin <span className="grad">Dashboard</span></h1></div>
        <button className="btn btn-ghost" data-testid="admin-logout" onClick={() => { localStorage.removeItem("admin_token"); setToken(""); }}>Logout</button>
      </div>

      {/* Tab switcher */}
      <div className="admin-filter-chips" style={{ marginBottom: "1.5rem" }} data-testid="admin-tabs">
        <button className={`fchip ${tab === "leads" ? "active" : ""}`} data-testid="admin-tab-leads" onClick={() => setTab("leads")}><i className="fa-solid fa-users"></i> Leads</button>
        <button className={`fchip ${tab === "payments" ? "active" : ""}`} data-testid="admin-tab-payments" onClick={() => setTab("payments")}><i className="fa-solid fa-credit-card"></i> Payments <span style={{ marginLeft: "0.4rem", fontSize: "0.7rem", padding: "0.1rem 0.45rem", borderRadius: "999px", background: "rgba(255,107,107,0.15)", color: "#ff9d9d", border: "1px solid rgba(255,107,107,0.3)" }}>TEST MODE</span></button>
      </div>

      {tab === "payments" ? (
        <AdminPayments token={token} pushToast={pushToast} />
      ) : (<>

      {stats && (
        <div className="admin-stat-grid" data-testid="admin-stats">
          <div className="stat"><div className="num">{stats.total}</div><div className="lbl">Total</div></div>
          {STATUS_KEYS.map((k) => (
            <div className="stat" key={k}><div className="num">{(stats.by_status && stats.by_status[k]) || 0}</div><div className="lbl">{STATUS_LABELS[k]}</div></div>
          ))}
        </div>
      )}

      {/* Search + filter bar */}
      <div className="admin-toolbar" data-testid="admin-toolbar">
        <div className="admin-search">
          <i className="fa-solid fa-magnifying-glass"></i>
          <input
            type="text"
            placeholder="Search by name, email, phone, or course/country…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            data-testid="admin-search-input"
          />
          {search && <button className="admin-search-clear" onClick={() => setSearch("")} data-testid="admin-search-clear" aria-label="Clear">×</button>}
        </div>
        <div className="admin-filter-chips" data-testid="admin-filter-chips">
          <button className={`fchip ${statusFilter === "all" ? "active" : ""}`} data-testid="filter-all" onClick={() => setStatusFilter("all")}>All</button>
          {STATUS_KEYS.map((k) => (
            <button key={k} className={`fchip ${statusFilter === k ? "active" : ""}`} data-testid={`filter-${k}`} onClick={() => setStatusFilter(k)}>{STATUS_LABELS[k]}</button>
          ))}
        </div>
      </div>

      {loading ? <p>Loading…</p> : (
        <div style={{ overflowX: "auto" }}>
          <table className="admin-table" data-testid="admin-leads-table">
            <thead><tr><th>When</th><th>Name</th><th>Contact</th><th>Goal</th><th>Source</th><th>Status</th><th>Action</th></tr></thead>
            <tbody>
              {filtered.length === 0 ? <tr><td colSpan="7" style={{ textAlign: "center", color: "var(--text-muted)" }}>No leads match the current search / filter.</td></tr> :
                filtered.map((l) => (
                  <tr key={l.id} data-testid={`lead-row-${l.id}`}>
                    <td className="mono" style={{ fontSize: "0.78rem" }}>{(l.created_at || "").slice(0, 16).replace("T", " ")}</td>
                    <td>
                      <strong>{l.name}</strong>
                      <br /><span style={{ color: "var(--text-muted)", fontSize: "0.78rem" }}>{l.level}</span>
                      {l.admin_notes ? <span title="Has admin notes" style={{ marginLeft: "0.4rem", color: "var(--brand-secondary)" }}>📝</span> : null}
                    </td>
                    <td className="mono" style={{ fontSize: "0.82rem" }}>{l.email}<br />{l.phone}</td>
                    <td>{l.goal || "—"}</td>
                    <td><span className="mono" style={{ fontSize: "0.75rem", color: "var(--text-secondary)" }}>{l.source}</span></td>
                    <td><span className={`status-chip status-${l.status}`}>{STATUS_LABELS[l.status] || l.status}</span></td>
                    <td style={{ whiteSpace: "nowrap" }}>
                      <button className="btn btn-ghost" style={{ padding: "0.35rem 0.7rem", fontSize: "0.78rem", marginRight: "0.4rem" }} data-testid={`view-lead-${l.id}`} onClick={() => setViewLead(l)}>View</button>
                      <select value={STATUS_KEYS.includes(l.status) ? l.status : ""} onChange={(e) => setStatus(l.id, e.target.value)} data-testid={`status-select-${l.id}`} style={{ background: "var(--bg-base)", border: "1px solid var(--border-subtle)", color: "var(--text-primary)", padding: "0.3rem 0.5rem", borderRadius: "6px", fontSize: "0.82rem" }}>
                        {!STATUS_KEYS.includes(l.status) && <option value="" disabled>{STATUS_LABELS[l.status] || l.status}</option>}
                        {STATUS_KEYS.map((k) => <option key={k} value={k}>{STATUS_LABELS[k]}</option>)}
                      </select>
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      )}

      {viewLead && <LeadDetailModal lead={viewLead} onClose={() => setViewLead(null)} onSaveNotes={saveNotes} />}
      </>)}
    </div></section>
  );
}


const PAYMENT_STATUS_LABELS = {
  created: "Created",
  verified_by_checkout: "Verified by Checkout",
  paid: "Paid",
  failed: "Failed",
  signature_failed: "Signature Failed",
};
const PAYMENT_STATUS_KEYS = ["created", "verified_by_checkout", "paid", "failed", "signature_failed"];

function fmtINR(paise) {
  if (paise == null) return "—";
  return "₹" + (paise / 100).toLocaleString("en-IN");
}

function AdminPayments({ token, pushToast }) {
  const [rows, setRows] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [view, setView] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [a, b] = await Promise.all([
        axios.get(`${API}/admin/payments`, { headers: { "X-Admin-Token": token } }),
        axios.get(`${API}/admin/payments/stats`, { headers: { "X-Admin-Token": token } }),
      ]);
      setRows(a.data.payments || []);
      setStats(b.data);
    } catch {
      pushToast("Failed to load payments", "err");
    } finally { setLoading(false); }
  };
  useEffect(() => { if (token) load(); }, [token]);

  const saveNotes = async (id, notes) => {
    try {
      await axios.patch(`${API}/admin/payments/${id}/notes`, { notes }, { headers: { "X-Admin-Token": token, "Content-Type": "application/json" } });
      pushToast("Notes saved");
      setView((v) => v && v.id === id ? { ...v, admin_notes: notes } : v);
      setRows((rs) => rs.map((r) => r.id === id ? { ...r, admin_notes: notes } : r));
    } catch { pushToast("Failed to save notes", "err"); }
  };

  const toggleAccess = async (id, next) => {
    try {
      await axios.patch(`${API}/admin/payments/${id}/access`, { access_granted: next }, { headers: { "X-Admin-Token": token, "Content-Type": "application/json" } });
      pushToast(next ? "Access granted" : "Access revoked");
      setView((v) => v && v.id === id ? { ...v, access_granted: next } : v);
      setRows((rs) => rs.map((r) => r.id === id ? { ...r, access_granted: next } : r));
    } catch { pushToast("Failed to update access", "err"); }
  };

  const filtered = rows.filter((r) => {
    if (statusFilter !== "all" && r.payment_status !== statusFilter) return false;
    if (!search.trim()) return true;
    const q = search.trim().toLowerCase();
    return (
      (r.student_name || "").toLowerCase().includes(q) ||
      (r.email || "").toLowerCase().includes(q) ||
      (r.phone || "").toLowerCase().includes(q) ||
      (r.course_name || "").toLowerCase().includes(q) ||
      (r.razorpay_order_id || "").toLowerCase().includes(q) ||
      (r.razorpay_payment_id || "").toLowerCase().includes(q)
    );
  });

  return (
    <div data-testid="admin-payments-panel">
      <div style={{ padding: "0.6rem 1rem", background: "rgba(255,107,107,0.08)", border: "1px solid rgba(255,107,107,0.25)", borderRadius: "8px", color: "#ffb3b3", fontSize: "0.85rem", marginBottom: "1rem" }} data-testid="payments-test-mode-banner">
        <i className="fa-solid fa-flask"></i> <strong>RAZORPAY TEST MODE</strong> — payments below are test transactions only. No real money involved.
      </div>

      {stats && (
        <div className="admin-stat-grid" data-testid="admin-payments-stats">
          <div className="stat"><div className="num">{stats.total}</div><div className="lbl">Total</div></div>
          {PAYMENT_STATUS_KEYS.map((k) => (
            <div className="stat" key={k}><div className="num">{(stats.by_status && stats.by_status[k]) || 0}</div><div className="lbl">{PAYMENT_STATUS_LABELS[k]}</div></div>
          ))}
          <div className="stat"><div className="num" style={{ color: "var(--brand-primary)" }}>{stats.access_granted}</div><div className="lbl">Access Granted</div></div>
        </div>
      )}

      <div className="admin-toolbar" data-testid="admin-payments-toolbar">
        <div className="admin-search">
          <i className="fa-solid fa-magnifying-glass"></i>
          <input type="text" placeholder="Search by name, email, phone, program, order/payment id…"
                 value={search} onChange={(e) => setSearch(e.target.value)} data-testid="admin-payments-search" />
          {search && <button className="admin-search-clear" onClick={() => setSearch("")} data-testid="admin-payments-search-clear" aria-label="Clear">×</button>}
        </div>
        <div className="admin-filter-chips" data-testid="admin-payments-filter-chips">
          <button className={`fchip ${statusFilter === "all" ? "active" : ""}`} data-testid="pf-all" onClick={() => setStatusFilter("all")}>All</button>
          {PAYMENT_STATUS_KEYS.map((k) => (
            <button key={k} className={`fchip ${statusFilter === k ? "active" : ""}`} data-testid={`pf-${k}`} onClick={() => setStatusFilter(k)}>{PAYMENT_STATUS_LABELS[k]}</button>
          ))}
        </div>
      </div>

      {loading ? <p>Loading…</p> : (
        <div style={{ overflowX: "auto" }}>
          <table className="admin-table" data-testid="admin-payments-table">
            <thead><tr>
              <th>When</th><th>Student</th><th>Program</th><th>Total / Checkout / Deferred</th>
              <th>Order / Payment</th><th>Status</th><th>Access</th><th>Action</th>
            </tr></thead>
            <tbody>
              {filtered.length === 0 ? <tr><td colSpan="8" style={{ textAlign: "center", color: "var(--text-muted)" }}>No payments match the current search / filter.</td></tr> :
                filtered.map((r) => (
                  <tr key={r.id} data-testid={`payment-row-${r.id}`}>
                    <td className="mono" style={{ fontSize: "0.78rem" }}>{(r.created_at || "").slice(0, 16).replace("T", " ")}</td>
                    <td>
                      <strong>{r.student_name}</strong>
                      {r.admin_notes ? <span title="Has admin notes" style={{ marginLeft: "0.4rem", color: "var(--brand-secondary)" }}>📝</span> : null}
                      <br /><span className="mono" style={{ color: "var(--text-muted)", fontSize: "0.75rem" }}>{r.email}<br />{r.phone}</span>
                    </td>
                    <td>{r.course_name}</td>
                    <td style={{ fontSize: "0.82rem" }} data-testid={`amounts-${r.id}`}>
                      <div>Total: <strong>{fmtINR(r.total_program_fee)}</strong></div>
                      <div style={{ color: "var(--brand-primary)" }}>Checkout: <strong>{fmtINR(r.checkout_amount)}</strong></div>
                      {r.deferred_balance > 0 && <div style={{ color: "var(--brand-secondary)" }}>Deferred: <strong>{fmtINR(r.deferred_balance)}</strong></div>}
                    </td>
                    <td className="mono" style={{ fontSize: "0.72rem" }}>
                      <div title={r.razorpay_order_id}>{(r.razorpay_order_id || "—").slice(0, 18)}</div>
                      <div title={r.razorpay_payment_id} style={{ color: "var(--text-muted)" }}>{(r.razorpay_payment_id || "—").slice(0, 18)}</div>
                    </td>
                    <td><span className={`status-chip status-${r.payment_status}`}>{PAYMENT_STATUS_LABELS[r.payment_status] || r.payment_status}</span></td>
                    <td>{r.access_granted ? <span style={{ color: "var(--brand-primary)" }}>✓ Granted</span> : <span style={{ color: "var(--text-muted)" }}>— Locked</span>}</td>
                    <td>
                      <button className="btn btn-ghost" style={{ padding: "0.35rem 0.7rem", fontSize: "0.78rem" }} data-testid={`view-payment-${r.id}`} onClick={() => setView(r)}>View</button>
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      )}

      {view && <PaymentDetailModal payment={view} onClose={() => setView(null)} onSaveNotes={saveNotes} onToggleAccess={toggleAccess} />}
    </div>
  );
}

function PaymentDetailModal({ payment, onClose, onSaveNotes, onToggleAccess }) {
  const [notes, setNotes] = useState(payment.admin_notes || "");
  const [saving, setSaving] = useState(false);
  useEffect(() => { setNotes(payment.admin_notes || ""); }, [payment.id, payment.admin_notes]);

  const row = (k, v) => (
    <div style={{ display: "flex", justifyContent: "space-between", padding: "0.35rem 0", borderBottom: "1px dashed rgba(255,255,255,0.06)", fontSize: "0.85rem" }}>
      <span style={{ color: "var(--text-muted)" }}>{k}</span>
      <span className="mono" style={{ textAlign: "right", wordBreak: "break-all", maxWidth: "60%" }}>{v ?? "—"}</span>
    </div>
  );

  const save = async () => {
    if (notes.length > 2000) return;
    setSaving(true);
    try { await onSaveNotes(payment.id, notes); } finally { setSaving(false); }
  };

  return (
    <div className="modal-backdrop" onClick={(e) => e.target === e.currentTarget && onClose()} data-testid="payment-detail-modal">
      <div className="modal-shell" style={{ maxWidth: 720 }}>
        <button className="modal-close" onClick={onClose} aria-label="Close" data-testid="payment-modal-close">×</button>
        <span className="eyebrow"><i className="fa-solid fa-receipt"></i> Payment · TEST MODE</span>
        <h2 style={{ fontSize: "1.5rem", margin: "0.8rem 0 0.5rem" }}>{payment.course_name}</h2>

        <div style={{ marginTop: "1rem" }} data-testid="payment-detail-fields">
          {row("SASXL Payment ID", payment.id)}
          {row("Student", payment.student_name)}
          {row("Email", payment.email)}
          {row("Phone", payment.phone)}
          {row("Program", payment.course_name)}
          {row("Total Program Fee", fmtINR(payment.total_program_fee))}
          {row("Checkout Amount", fmtINR(payment.checkout_amount))}
          {row("Deferred Balance", fmtINR(payment.deferred_balance))}
          {row("Currency", payment.currency)}
          {row("Razorpay Order ID", payment.razorpay_order_id)}
          {row("Razorpay Payment ID", payment.razorpay_payment_id)}
          {row("Payment Status", PAYMENT_STATUS_LABELS[payment.payment_status] || payment.payment_status)}
          {row("Signature Verified", String(!!payment.signature_verified))}
          {row("Webhook Confirmed", String(!!payment.webhook_confirmed))}
          {row("Payment Method", payment.method || "—")}
          {row("Created At", (payment.created_at || "").replace("T", " ").slice(0, 19))}
          {row("Verified At", (payment.verified_at || "").replace("T", " ").slice(0, 19) || "—")}
          {row("Access Granted", payment.access_granted ? "✓ Granted" : "— Locked")}
        </div>

        <div style={{ marginTop: "1.25rem", display: "flex", gap: "0.6rem", flexWrap: "wrap" }}>
          <button
            className={payment.access_granted ? "btn btn-ghost" : "btn btn-primary"}
            data-testid="toggle-access-btn"
            onClick={() => onToggleAccess(payment.id, !payment.access_granted)}>
            <i className="fa-solid fa-key"></i> {payment.access_granted ? "Revoke Access" : "Grant Access (Manual)"}
          </button>
          {payment.lead_id && <span style={{ fontSize: "0.75rem", color: "var(--text-muted)", alignSelf: "center" }}>Linked lead: {payment.lead_id.slice(0, 8)}…</span>}
        </div>

        <div style={{ marginTop: "1.25rem" }}>
          <label style={{ fontSize: "0.78rem", color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.1em" }}>Admin Notes (internal)</label>
          <textarea
            data-testid="payment-notes-input"
            value={notes} onChange={(e) => setNotes(e.target.value)}
            maxLength={2000} rows={4}
            style={{ width: "100%", background: "var(--bg-base)", border: "1px solid var(--border-subtle)", color: "var(--text-primary)", padding: "0.6rem 0.8rem", borderRadius: "8px", fontFamily: "inherit", marginTop: "0.4rem" }}
            placeholder="e.g. Payment verified. Student contacted. Access pending."
          />
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: "0.4rem" }}>
            <span style={{ fontSize: "0.75rem", color: "var(--text-muted)" }}>{notes.length}/2000</span>
            <button className="btn btn-primary" style={{ padding: "0.5rem 1rem" }} data-testid="payment-notes-save" onClick={save} disabled={saving}>
              {saving ? "Saving…" : "Save Notes"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function LeadDetailModal({ lead, onClose, onSaveNotes }) {
  const [notes, setNotes] = useState(lead.admin_notes || "");
  const [saving, setSaving] = useState(false);
  const dirty = (notes || "") !== (lead.admin_notes || "");

  const handleSave = async () => {
    setSaving(true);
    await onSaveNotes(lead.id, notes);
    setSaving(false);
  };

  const row = (label, value) => (
    <div className="detail-row" key={label}>
      <div className="detail-label">{label}</div>
      <div className="detail-value">{value || <span style={{ color: "var(--text-muted)" }}>—</span>}</div>
    </div>
  );

  return (
    <div className="modal-backdrop" data-testid="lead-detail-modal" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="modal-shell" style={{ maxWidth: 640 }}>
        <button className="modal-close" data-testid="lead-modal-close" onClick={onClose} aria-label="Close">×</button>
        <span className="eyebrow"><i className="fa-solid fa-id-card"></i> Lead Details</span>
        <h2 style={{ fontSize: "1.5rem", margin: "0.75rem 0 0.4rem" }}>{lead.name}</h2>
        <div style={{ marginBottom: "1.25rem" }}>
          <span className={`status-chip status-${lead.status}`}>{STATUS_LABELS[lead.status] || lead.status}</span>
        </div>
        <div className="detail-grid">
          {row("Email", lead.email)}
          {row("Phone", lead.phone)}
          {row("Experience Level", lead.level)}
          {row("Goal (Course · Country)", lead.goal)}
          {row("Message", lead.message)}
          {row("Source", lead.source)}
          {row("Submitted (UTC)", (lead.created_at || "").replace("T", " ").slice(0, 19))}
          {row("Lead ID", <span className="mono" style={{ fontSize: "0.78rem" }}>{lead.id}</span>)}
        </div>

        <div style={{ marginTop: "1.5rem" }}>
          <div className="detail-label" style={{ marginBottom: "0.5rem" }}>Admin Notes <span style={{ color: "var(--text-muted)", fontSize: "0.72rem", textTransform: "none", letterSpacing: 0 }}>(internal only · never sent to student)</span></div>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="e.g. Called student. Interested in Live batch. Roadmap sent. Follow up Friday."
            data-testid="lead-notes-textarea"
            style={{ width: "100%", minHeight: 120, background: "rgba(5,8,20,0.6)", border: "1px solid var(--border-subtle)", color: "var(--text-primary)", padding: "0.85rem 1rem", borderRadius: 8, fontFamily: "Inter, sans-serif", fontSize: "0.92rem", resize: "vertical" }}
            maxLength={2000}
          />
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: "0.75rem", gap: "0.75rem", flexWrap: "wrap" }}>
            <span style={{ color: "var(--text-muted)", fontSize: "0.75rem" }}>{notes.length} / 2000 characters</span>
            <button className="btn btn-primary" data-testid="lead-notes-save" onClick={handleSave} disabled={saving || !dirty} style={{ padding: "0.55rem 1.1rem", fontSize: "0.85rem" }}>
              <i className="fa-solid fa-floppy-disk"></i> {saving ? "Saving…" : "Save Notes"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ============ STATIC PAGES ============
function PrivacyPage() {
  return (
    <section className="page-hero"><div className="container reveal" style={{ maxWidth: 820 }} data-testid="privacy-policy-page">
      <span className="eyebrow"><i className="fa-solid fa-shield-halved"></i> Legal</span>
      <h1>Privacy <span className="grad">Policy</span></h1>
      <p style={{ color: "var(--text-muted)" }}>Last updated: 23 August 2026</p>
      <div style={{ color: "var(--text-secondary)", marginTop: "2rem", lineHeight: 1.8 }}>

        <h3 style={{ color: "var(--text-primary)", marginTop: "1.5rem" }}>1. Information We Collect</h3>
        <p>When you submit an enquiry through our website, Meta lead form or another SASXL contact channel, we may collect information such as your name, email address, phone number, qualification, graduation year, current career status, SAS knowledge, training availability, preferred joining timeline, goals and other information you voluntarily provide.</p>

        <h3 style={{ color: "var(--text-primary)", marginTop: "1.5rem" }}>2. How We Use Your Information</h3>
        <p>We use this information to:</p>
        <ul style={{ paddingLeft: "1.2rem", margin: "0.4rem 0 0.6rem" }}>
          <li>Respond to your enquiry</li>
          <li>Assess program eligibility</li>
          <li>Provide information about SASXL Clinical SAS training programs</li>
          <li>Schedule counselling or orientation calls</li>
          <li>Follow up regarding enrollment</li>
          <li>Improve our enquiry and support processes</li>
        </ul>

        <h3 style={{ color: "var(--text-primary)", marginTop: "1.5rem" }}>3. Service Providers and Data Sharing</h3>
        <p>We do not sell your personal information.</p>
        <p>We may share information with service providers, such as Meta and our CRM or communication providers, only when necessary to collect, store, manage and respond to your enquiry. These providers process information according to their applicable terms and data-protection obligations.</p>
        <p>We may also disclose information when required by law or to protect our legal rights.</p>

        <h3 style={{ color: "var(--text-primary)", marginTop: "1.5rem" }}>4. Data Retention</h3>
        <p>Lead records may be retained for up to 24 months unless a longer period is required for legal or operational reasons. You may request correction or deletion of your information by emailing <span className="mono" style={{ color: "var(--brand-primary)" }}>hello@sasxl.com</span>.</p>

        <h3 style={{ color: "var(--text-primary)", marginTop: "1.5rem" }}>5. Communications and Consent</h3>
        <p>By submitting an enquiry, you consent to SASXL contacting you by phone, email or messaging services regarding the program you expressed interest in. You may ask us to stop promotional follow-ups at any time.</p>

        <h3 style={{ color: "var(--text-primary)", marginTop: "1.5rem" }}>6. Cookies and Website Storage</h3>
        <p>We use limited browser storage and cookies necessary for website functionality, security and session management. If analytics or advertising technologies are introduced, this policy will be updated accordingly.</p>

        <h3 style={{ color: "var(--text-primary)", marginTop: "1.5rem" }}>7. Data Security</h3>
        <p>We use reasonable administrative and technical measures to protect personal information. However, no internet transmission or electronic storage method can be guaranteed to be completely secure.</p>

        <h3 style={{ color: "var(--text-primary)", marginTop: "1.5rem" }}>8. Contact</h3>
        <p>For privacy questions, correction requests or deletion requests, email <span className="mono" style={{ color: "var(--brand-primary)" }}>hello@sasxl.com</span>.</p>
      </div>
    </div></section>
  );
}

function TermsPage() {
  return (
    <section className="page-hero"><div className="container reveal" style={{ maxWidth: 820 }}>
      <span className="eyebrow"><i className="fa-solid fa-file-contract"></i> Legal</span>
      <h1>Terms of <span className="grad">Service</span></h1>
      <p>Last updated: {new Date().toLocaleDateString()}</p>
      <div style={{ color: "var(--text-secondary)", marginTop: "2rem", lineHeight: 1.8 }}>
        <h3 style={{ color: "var(--text-primary)", marginTop: "1.5rem" }}>1. Enrollment</h3>
        <p>Course enrollment is confirmed only after full or first EMI payment is received. Access to the LMS is provided within 24 hours of payment confirmation.</p>
        <h3 style={{ color: "var(--text-primary)", marginTop: "1.5rem" }}>2. Refunds</h3>
        <p>A 7-day cooling-off refund is available from the date of enrollment, provided no more than 2 live sessions have been attended. After this period, refunds are not offered.</p>
        <h3 style={{ color: "var(--text-primary)", marginTop: "1.5rem" }}>3. Intellectual Property</h3>
        <p>All course materials remain the property of SASXL. You may not redistribute or resell them.</p>
        <h3 style={{ color: "var(--text-primary)", marginTop: "1.5rem" }}>4. Placement Support</h3>
        <p>Placement assistance includes interview prep, mock interviews and referral introductions where available. It is not a guarantee of employment.</p>
        <h3 style={{ color: "var(--text-primary)", marginTop: "1.5rem" }}>5. Contact</h3>
        <p>For questions about these terms, email <span className="mono" style={{ color: "var(--brand-primary)" }}>hello@sasxl.com</span>.</p>
      </div>
    </div></section>
  );
}

// ============ PLACEMENT PAGE ============
function PlacementPage({ openLead }) {
  const stages = [
    ["fa-file-lines","Resume Preparation","Pharma-recruiter-formatted resume. ATS-keyword optimized. Quantified bullets. 2 review rounds with placement team."],
    ["fa-pen-ruler","LinkedIn Rebuild","Profile audit, headline rewrite, banner design, recruiter-keyword optimization. Get found by IQVIA, Cognizant recruiters."],
    ["fa-folder-tree","Real Project Portfolio","4 capstone projects on GitHub — SDTM mapping, ADaM construction, TLF generation, Define-XML authoring. Hiring managers love this."],
    ["fa-comments","Mock Interviews","Realistic 1-hour mock interviews simulating IQVIA, Parexel & Cognizant loops. Recorded · played back · feedback-driven."],
    ["fa-handshake","Recruiter Referrals","Direct introductions to hiring managers in our 70+ alumni network. Bypass cold applications wherever possible."],
    ["fa-bullseye","Job Application Strategy","Targeted applications · cover letter templates · follow-up cadence · offer evaluation & salary negotiation playbook."],
  ];
  return (
    <>
      <section className="page-hero grid-bg noise"><div className="container reveal">
        <span className="eyebrow"><i className="fa-solid fa-briefcase"></i> Placement Support</span>
        <h1>From Capstone to <span className="grad">Job Offer</span>.</h1>
        <p>Our placement process isn't an add-on — it's baked into months 5 and 6 of your training. Here's exactly what we do to get you hired.</p>
      </div></section>
      <section style={{ paddingTop: "2rem" }} data-testid="placement-stages">
        <div className="container">
          <div className="modules reveal" data-testid="placement-stages-grid">
            {stages.map(([ic, h, d], i) => (
              <div className="module" key={i} data-testid={`placement-stage-${i}`}>
                <span className="num">STAGE 0{i+1}</span>
                <h3><i className={`fa-solid ${ic}`} style={{ color: "var(--brand-primary)", marginRight: "0.6rem" }}></i>{h}</h3>
                <p style={{ color: "var(--text-secondary)", marginTop: "0.5rem" }}>{d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
      <section data-testid="placement-fee-section"><div className="container">
        <div className="fee-banner reveal">
          <div className="fee-banner-left">
            <span className="eyebrow"><i className="fa-solid fa-handshake"></i> Our Promise</span>
            <h3>Pay 75% of Fees <span className="grad">Only After</span> Your Job Offer.</h3>
            <p>We invest in your placement journey before you fully invest in us. That's how confident we are.</p>
          </div>
          <div className="fee-banner-right">
            <button className="btn btn-primary" data-testid="placement-cta" onClick={() => openLead("Placement Page")}><i className="fa-solid fa-calendar-check"></i> Book Free Career Call</button>
          </div>
        </div>
      </div></section>
      <section style={{ paddingTop: 0 }}><div className="container">
        <div className="disclaimer reveal">
          <i className="fa-solid fa-circle-info"></i>
          <p><strong>Placement Disclaimer:</strong> SASXL provides comprehensive placement assistance — resume preparation, mock interviews, referrals, and interview readiness. Job offers depend on individual candidate skills, interview performance, market conditions, and employer requirements. We do not guarantee employment.</p>
        </div>
      </div></section>
    </>
  );
}

// ============ RESOURCES PAGE ============
function ResourcesPage({ pushToast }) {
  return (
    <>
      <section className="page-hero grid-bg noise"><div className="container reveal">
        <span className="eyebrow"><i className="fa-solid fa-download"></i> Free Downloads</span>
        <h1>Free <span className="grad">Career Resources</span>.</h1>
        <p>Hand-crafted by Praveen and the SASXL placement team. Used by 1500+ students. Yours free.</p>
      </div></section>
      <section style={{ paddingTop: "2rem" }} data-testid="resources-page">
        <div className="container">
          <div className="resource-grid reveal">
            {RESOURCES.map((r) => (
              <ResourceForm key={r.id} resource={r} pushToast={pushToast} />
            ))}
          </div>
        </div>
      </section>
    </>
  );
}

function ResourceForm({ resource, pushToast }) {
  const [form, setForm] = useState({ name: "", email: "", phone: "" });
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone) { pushToast("Fill all fields", "err"); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/leads`, { name: form.name, email: form.email, phone: form.phone, level: "", goal: resource.title, message: "Resource download", source: `resource-${resource.id}` });
      setDone(true);
      pushToast("Check WhatsApp & Email for the PDF!");
    } catch { pushToast("Failed. Try WhatsApp.", "err"); }
    finally { setBusy(false); }
  };
  return (
    <div className="resource-card" data-testid={`resource-page-${resource.id}`}>
      <div className="resource-icon"><i className={`fa-solid ${resource.icon}`}></i></div>
      <span className="resource-tag">{resource.tag}</span>
      <h3>{resource.title}</h3>
      <p>{resource.desc}</p>
      {done ? (
        <div style={{ background: "rgba(57,255,20,0.1)", border: "1px solid var(--brand-secondary)", borderRadius: "10px", padding: "1rem", marginTop: "1rem" }}>
          <strong style={{ color: "var(--brand-secondary)" }}><i className="fa-solid fa-circle-check"></i> Sent!</strong>
          <p style={{ margin: "0.4rem 0 0", fontSize: "0.85rem", color: "var(--text-secondary)" }}>Check your WhatsApp & email in the next 5 minutes. Didn't get it? <a onClick={() => openWA(`Hi! I requested the ${resource.title} PDF — please share.`)} style={{ color: "var(--brand-primary)", cursor: "pointer" }}>Ping us on WhatsApp</a>.</p>
        </div>
      ) : (
        <form onSubmit={submit} style={{ marginTop: "1rem" }}>
          <div className="field" style={{ marginBottom: "0.6rem" }}><input type="text" required placeholder="Name" value={form.name} onChange={set("name")} data-testid={`rsrc-name-${resource.id}`} /></div>
          <div className="field" style={{ marginBottom: "0.6rem" }}><input type="email" required placeholder="Email" value={form.email} onChange={set("email")} data-testid={`rsrc-email-${resource.id}`} /></div>
          <div className="field" style={{ marginBottom: "0.6rem" }}><input type="tel" required placeholder="Mobile" value={form.phone} onChange={set("phone")} data-testid={`rsrc-phone-${resource.id}`} /></div>
          <button type="submit" className="btn btn-primary" style={{ width: "100%", justifyContent: "center" }} disabled={busy} data-testid={`rsrc-submit-${resource.id}`}>
            <i className="fa-solid fa-download"></i> {busy ? "Sending…" : "Get Free PDF"}
          </button>
        </form>
      )}
    </div>
  );
}

// ============ COUNTRY LANDING PAGES ============
const COUNTRY_DATA = {
  india: { name: "India", flag: "🇮🇳", salary: "₹4.5L → ₹24L", hubs: "Hyderabad · Bengaluru · Pune · Chennai", local: "Live weekend cohort (IST friendly). EMI options. Pay 75% after Offer." },
  usa: { name: "USA", flag: "🇺🇸", salary: "$72K → $165K", hubs: "Boston · Raleigh · NYC · SF · Chicago", local: "Late-evening IST = early morning EST. H1B-friendly cohort. CDISC SDTM/ADaM aligned with FDA." },
  uk: { name: "UK", flag: "🇬🇧", salary: "£42K → £95K", hubs: "London · Cambridge · Manchester · Edinburgh", local: "MHRA-aligned curriculum. Convenient evening sessions for UK time." },
  canada: { name: "Canada", flag: "🇨🇦", salary: "CAD 75K → 160K", hubs: "Toronto · Montreal · Vancouver", local: "Health Canada submission patterns. Express Entry & PR pathway alignment." },
  australia: { name: "Australia", flag: "🇦🇺", salary: "AUD 90K → 175K", hubs: "Melbourne · Sydney · Brisbane", local: "TGA-aligned content. Friendly cohort timing for AEST." },
};
function CountryPage({ openLead }) {
  const country = window.location.pathname.split("/").pop().toLowerCase();
  const data = COUNTRY_DATA[country] || COUNTRY_DATA.india;
  return (
    <>
      <section className="page-hero grid-bg noise"><div className="container reveal">
        <span className="eyebrow"><i className="fa-solid fa-earth-asia"></i> SAS Clinical Training · {data.flag} {data.name}</span>
        <h1>Clinical SAS Training for <span className="grad">{data.name}</span>-based learners.</h1>
        <p>Curriculum tailored for {data.name} job market. {data.local}</p>
        <div className="hero-actions" style={{ marginTop: "2rem" }}>
          <button className="btn btn-primary" data-testid="country-cta" onClick={() => openLead(`Country: ${data.name}`)}>
            <i className="fa-solid fa-calendar-check"></i> Book Free Career Consultation
          </button>
          <button className="btn btn-whatsapp" onClick={() => openWA(`Hi SASXL! I'm based in ${data.name} and would like to know about Clinical SAS training for ${data.name}.`)}>
            <i className="fa-brands fa-whatsapp"></i> WhatsApp
          </button>
        </div>
      </div></section>
      <section style={{ paddingTop: "2rem" }}><div className="container">
        <div className="hero-stats reveal" data-testid="country-stats">
          <div className="stat"><div className="num">{data.salary}</div><div className="lbl">Salary Range</div></div>
          <div className="stat"><div className="num">{data.flag}</div><div className="lbl">{data.name} Cohort</div></div>
          <div className="stat"><div className="num">6 mo</div><div className="lbl">To Job Offer</div></div>
          <div className="stat"><div className="num">25/75</div><div className="lbl">Pay-After-Offer</div></div>
        </div>
        <div style={{ marginTop: "3rem" }} className="reveal">
          <h3 style={{ fontFamily: "Outfit", fontSize: "1.4rem", marginBottom: "0.6rem" }}>Top Hiring Hubs · {data.name}</h3>
          <p style={{ color: "var(--text-secondary)" }}>{data.hubs}</p>
        </div>
      </div></section>
    </>
  );
}

// ============ PLACEMENT PAGE END ============

// ============ APP ============
if (typeof window !== "undefined" && "scrollRestoration" in window.history) {
  window.history.scrollRestoration = "manual";
}
function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    const doScroll = () => window.scrollTo(0, 0);
    doScroll();
    const raf = requestAnimationFrame(doScroll);
    const t = setTimeout(doScroll, 120);
    return () => { cancelAnimationFrame(raf); clearTimeout(t); };
  }, [pathname]);
  return null;
}

function App() {
  const [toast, setToast] = useState(null);
  const [leadOpen, setLeadOpen] = useState(false);
  const [leadSource, setLeadSource] = useState("Booking");
  const [exitOpen, setExitOpen] = useState(false);
  const pushToast = (msg, kind) => setToast({ msg, kind });
  const openLead = (source) => { setLeadSource(source || "Booking"); setLeadOpen(true); };
  useExitIntent(() => setExitOpen(true), true);

  return (
    <BrowserRouter>
      <ScrollToTop />
      <Routes>
        <Route path="/" element={<Layout openLead={openLead}><HomePage pushToast={pushToast} openLead={openLead} /></Layout>} />
        <Route path="/courses" element={<Layout openLead={openLead}><CoursesPage /></Layout>} />
        <Route path="/career" element={<Layout openLead={openLead}><CareerPage /></Layout>} />
        <Route path="/projects" element={<Layout openLead={openLead}><ProjectsPage /></Layout>} />
        <Route path="/mentors" element={<Layout openLead={openLead}><MentorsPage /></Layout>} />
        <Route path="/contact" element={<Layout openLead={openLead}><ContactPage pushToast={pushToast} /></Layout>} />
        <Route path="/placement" element={<Layout openLead={openLead}><PlacementPage openLead={openLead} /></Layout>} />
        <Route path="/resources" element={<Layout openLead={openLead}><ResourcesPage pushToast={pushToast} /></Layout>} />
        <Route path="/training/:country" element={<Layout openLead={openLead}><CountryPage openLead={openLead} /></Layout>} />
        <Route path="/admin" element={<Layout openLead={openLead}><AdminPage pushToast={pushToast} /></Layout>} />
        <Route path="/privacy" element={<Layout openLead={openLead}><PrivacyPage /></Layout>} />
        <Route path="/privacy-policy" element={<Layout openLead={openLead}><PrivacyPage /></Layout>} />
        <Route path="/terms" element={<Layout openLead={openLead}><TermsPage /></Layout>} />
        <Route path="*" element={<Layout openLead={openLead}><HomePage pushToast={pushToast} openLead={openLead} /></Layout>} />
      </Routes>
      <QuickLeadModal open={leadOpen} onClose={() => setLeadOpen(false)} source={leadSource} pushToast={pushToast} />
      <QuickLeadModal open={exitOpen} onClose={() => setExitOpen(false)} source="Exit Intent" pushToast={pushToast}
        title="Wait — Free SAS Clinical Career Guide 📕"
        subtitle="Before you go, grab our free Career Roadmap PDF + Interview Questions cheat sheet. Just leave your details." />
      {toast && <Toast msg={toast.msg} kind={toast.kind} onClose={() => setToast(null)} />}
    </BrowserRouter>
  );
}

export default App;
